import React, { useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useSearchParams, useNavigate } from 'react-router-dom'
import {
  Package,
  Search,
  Eye,
  Edit2,
  Trash2,
  Plus,
  Loader2,
  AlertCircle,
  AlertTriangle,
  X,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  Users,
  Building2,
  GraduationCap,
  Star,
  Check,
  ToggleLeft,
  ToggleRight,
  Layers,
  DollarSign,
  Tag,
  Zap,
  Banknote,
  CheckSquare,
  Square,
  BookOpen,
  ListTree,
  FileDown,
  Save,
  Copy,
} from 'lucide-react'
import {
  adminApi,
  AdminProduct,
  ProductStatus,
  PricingType,
  CreateProductRequest,
  UpdateProductRequest,
  CreateProductPricingRequest,
  ProductPricing,
  TARGET_AUDIENCE_OPTIONS,
  OrganizationSimple,
  ProductOrganization,
  PricingTemplate,
  PricingOptionConfig,
  AdminLcTrialCurriculum,
} from '@/api/admin'
import * as configApi from '@/api/configuration'
import CurriculumTreeViewer from '@/components/common/CurriculumTreeViewer'
import { ConfigurationSummary, ConfigurationEditModal } from '@/components/configuration'
import ProductModeSelector from '@/components/configuration-v2/ProductModeSelector'
import type { LessonConfiguration } from '@/types/configuration'
import type { SubscriptionType } from '@/types/api'
import { toast } from 'sonner'

// Constants
const PRODUCT_STATUSES: ProductStatus[] = ['draft', 'active', 'archived', 'discontinued']
const PRICING_TYPES: PricingType[] = ['one_time', 'subscription']
// billing_period_months 중심 옵션 (source of truth)
// 구독용 결제 주기 옵션
const SUBSCRIPTION_CYCLE_OPTIONS: { months: number; label: string }[] = [
  { months: 1, label: '월' },
  { months: 3, label: '분기' },
  { months: 12, label: '1년' },
  { months: 24, label: '2년' },
]

// Labels
const PRODUCT_STATUS_LABELS: Record<ProductStatus, string> = {
  draft: '초안',
  active: '활성',
  archived: '보관',
  discontinued: '종료',
}

const PRICING_TYPE_LABELS: Record<PricingType, string> = {
  one_time: '일시불',
  subscription: '구독',
}

// billing_period_months → 라벨 파생 함수
function billingPeriodLabel(months: number): string {
  switch (months) {
    case 0: return '평생'
    case 1: return '1개월'
    case 3: return '3개월'
    case 6: return '6개월'
    case 12: return '1년'
    case 24: return '2년'
    default: return `${months}개월`
  }
}

// 구독용 결제주기 라벨 (짧은 표현)
function billingCycleLabel(months: number): string {
  switch (months) {
    case 0: return '평생'
    case 1: return '월간'
    case 3: return '분기'
    case 12: return '연간'
    default: return `${months}개월`
  }
}

// 좌석수 옵션
const SEAT_OPTIONS: number[] = [1, 10, 20, 50, 100, 150, 300, 500, 1000, 2000, 5000, 100000]

// 일시불용 사용 기간 옵션 (months만 사용)
const ONE_TIME_DURATION_OPTIONS: { months: number; label: string }[] = [
  { months: 1, label: '1개월' },
  { months: 3, label: '3개월' },
  { months: 6, label: '6개월' },
  { months: 12, label: '1년' },
  { months: 24, label: '2년' },
  { months: 0, label: '평생' },
]

// Colors
const PRODUCT_STATUS_COLORS: Record<ProductStatus, string> = {
  draft: 'bg-slate-100 text-slate-600',
  active: 'bg-green-100 text-green-700',
  archived: 'bg-yellow-100 text-yellow-700',
  discontinued: 'bg-red-100 text-red-700',
}

// Target Audience Icons
const TARGET_AUDIENCE_ICONS: Record<string, React.ElementType> = {
  individual: Users,
  parent: Users,
  student: GraduationCap,
  business: Building2,
  academy: GraduationCap,
  school: GraduationCap,
  all: Users,
}

// ============================================================================
// Pricing Form Modal
// ============================================================================
interface PricingFormModalProps {
  product: AdminProduct
  pricing: ProductPricing | null
  onClose: () => void
  onSave: (data: CreateProductPricingRequest) => void
  isLoading: boolean
}

function PricingFormModal({ product, pricing, onClose, onSave, isLoading }: PricingFormModalProps) {
  const [formData, setFormData] = useState<CreateProductPricingRequest>({
    productId: product.id,
    name: pricing?.name ?? '',
    pricingType: pricing?.pricingType ?? 'subscription',
    billingPeriodMonths: pricing?.billingPeriodMonths ?? 1,
    price: pricing?.price ?? 0,
    currency: pricing?.currency ?? 'KRW',
    originalPrice: pricing?.originalPrice ?? undefined,
    discountPercentage: pricing?.discountPercentage ?? undefined,
    maxSeats: pricing?.maxSeats ?? 0, // 새 항목은 0 (placeholder 표시), 저장 시 1로 처리
    hasTrial: pricing?.hasTrial ?? false,
    trialDays: pricing?.trialDays ?? 0,
    isActive: pricing?.isActive ?? true,
    isDefault: pricing?.isDefault ?? false,
    displayOrder: pricing?.displayOrder ?? 0,
    isNegotiable: pricing?.isNegotiable ?? false,
  })
  const [showTemplateSelect, setShowTemplateSelect] = useState(false)

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    onSave(formData)
  }

  // 할인율 자동 계산
  const calculateDiscount = () => {
    if (formData.originalPrice && formData.price && formData.originalPrice > formData.price) {
      const discount = Math.round(((formData.originalPrice - formData.price) / formData.originalPrice) * 100)
      setFormData({ ...formData, discountPercentage: discount })
    }
  }

  // 템플릿에서 불러오기 → 폼 데이터 채움
  const handleLoadFromTemplate = (data: CreateProductPricingRequest) => {
    setFormData(data)
    setShowTemplateSelect(false)
  }

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white flex items-center justify-between p-6 border-b border-slate-200 z-10">
          <div>
            <h2 className="text-xl font-bold text-slate-900">
              {pricing ? '가격 옵션 수정' : '새 가격 옵션 추가'}
            </h2>
            <p className="text-sm text-slate-500 mt-1">{product.name}</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {/* 템플릿에서 불러오기 (새 항목 추가 시만 표시) */}
          {!pricing && (
            <button
              type="button"
              onClick={() => setShowTemplateSelect(true)}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-blue-700 bg-blue-50 border border-blue-200 border-dashed rounded-lg hover:bg-blue-100 transition-colors"
            >
              <FileDown className="w-4 h-4" />
              템플릿에서 불러오기
            </button>
          )}

          {/* 기본 정보 */}
          <div>
            <label className="label">가격 옵션명 *</label>
            <input
              type="text"
              className="input"
              placeholder="예: 월간 구독, 연간 구독, 평생 이용권"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">가격 유형 *</label>
              <select
                className="input"
                value={formData.pricingType}
                onChange={(e) => setFormData({ ...formData, pricingType: e.target.value as PricingType })}
              >
                {PRICING_TYPES.map((type) => (
                  <option key={type} value={type}>{PRICING_TYPE_LABELS[type]}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="label">{formData.pricingType === 'one_time' ? '사용 기간' : '결제 주기'}</label>
              {formData.pricingType === 'one_time' ? (
                <select
                  className="input"
                  value={formData.billingPeriodMonths}
                  onChange={(e) => {
                    const months = parseInt(e.target.value)
                    setFormData({ ...formData, billingPeriodMonths: months })
                  }}
                >
                  {ONE_TIME_DURATION_OPTIONS.map((opt) => (
                    <option key={opt.months} value={opt.months}>{opt.label}</option>
                  ))}
                </select>
              ) : (
                <select
                  className="input"
                  value={formData.billingPeriodMonths}
                  onChange={(e) => {
                    const months = parseInt(e.target.value)
                    setFormData({ ...formData, billingPeriodMonths: months })
                  }}
                >
                  {SUBSCRIPTION_CYCLE_OPTIONS.map((opt) => (
                    <option key={opt.months} value={opt.months}>{opt.label}</option>
                  ))}
                </select>
              )}
            </div>
          </div>

          {/* 좌석수 */}
          <div>
            <label className="label">좌석수</label>
            <select
              className="input"
              value={formData.maxSeats}
              onChange={(e) => setFormData({ ...formData, maxSeats: parseInt(e.target.value) })}
            >
              {SEAT_OPTIONS.map((seat) => (
                <option key={seat} value={seat}>{seat.toLocaleString()}석</option>
              ))}
            </select>
          </div>

          {/* 가격 정보 */}
          <div className="bg-slate-50 p-4 rounded-lg space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                <DollarSign className="w-4 h-4" />
                가격 설정
              </h3>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  className="w-4 h-4 rounded border-slate-300"
                  checked={formData.isNegotiable}
                  onChange={(e) => setFormData({
                    ...formData,
                    isNegotiable: e.target.checked,
                    // 별도 협의 체크 시 가격 관련 필드 초기화
                    ...(e.target.checked && { price: 0, originalPrice: undefined, discountPercentage: undefined })
                  })}
                />
                <span className="text-sm font-medium text-slate-700">별도 협의</span>
              </label>
            </div>
            {!formData.isNegotiable && (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="label">판매 가격 (원) *</label>
                    <input
                      type="number"
                      className="input"
                      value={formData.price || ''}
                      onChange={(e) => setFormData({ ...formData, price: parseInt(e.target.value) || 0 })}
                      onFocus={(e) => e.target.select()}
                      onBlur={calculateDiscount}
                      placeholder="0"
                      required
                    />
                  </div>
                  <div>
                    <label className="label">정가 (원)</label>
                    <input
                      type="number"
                      className="input"
                      placeholder="할인 전 가격"
                      value={formData.originalPrice ?? ''}
                      onChange={(e) => setFormData({ ...formData, originalPrice: parseInt(e.target.value) || undefined })}
                      onFocus={(e) => e.target.select()}
                      onBlur={calculateDiscount}
                    />
                  </div>
                </div>
                <div>
                  <label className="label">할인율 (%)</label>
                  <input
                    type="number"
                    className="input"
                    placeholder="자동 계산됨"
                    value={formData.discountPercentage ?? ''}
                    onChange={(e) => setFormData({ ...formData, discountPercentage: parseInt(e.target.value) || undefined })}
                    onFocus={(e) => e.target.select()}
                  />
                </div>
              </>
            )}
            {formData.isNegotiable && (
              <p className="text-sm text-slate-500 italic">별도 협의 가격입니다.</p>
            )}
          </div>

          {/* 무료 체험 */}
          <div className="bg-blue-50 p-4 rounded-lg space-y-3">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                className="w-5 h-5 rounded border-slate-300"
                checked={formData.hasTrial}
                onChange={(e) => setFormData({ ...formData, hasTrial: e.target.checked })}
              />
              <span className="font-medium text-slate-700">무료 체험 제공</span>
            </label>
            {formData.hasTrial && (
              <div>
                <label className="label">무료 체험 기간 (일)</label>
                <input
                  type="number"
                  className="input"
                  min={1}
                  max={30}
                  value={formData.trialDays || ''}
                  onChange={(e) => setFormData({ ...formData, trialDays: parseInt(e.target.value) || 0 })}
                  onFocus={(e) => e.target.select()}
                  placeholder="7"
                />
              </div>
            )}
          </div>

          {/* 옵션 */}
          <div className="flex flex-wrap gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                className="w-5 h-5 rounded border-slate-300"
                checked={formData.isActive}
                onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
              />
              <span className="text-sm font-medium text-slate-700">활성화</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                className="w-5 h-5 rounded border-slate-300"
                checked={formData.isDefault}
                onChange={(e) => setFormData({ ...formData, isDefault: e.target.checked })}
              />
              <span className="text-sm font-medium text-slate-700">기본 옵션 (추천)</span>
            </label>
          </div>

          <div>
            <label className="label">표시 순서</label>
            <input
              type="number"
              className="input w-24"
              value={formData.displayOrder || ''}
              onChange={(e) => setFormData({ ...formData, displayOrder: parseInt(e.target.value) || 0 })}
              onFocus={(e) => e.target.select()}
              placeholder="0"
            />
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-slate-200">
            <button type="button" onClick={onClose} className="btn-secondary btn-md" disabled={isLoading}>
              취소
            </button>
            <button type="submit" className="btn-primary btn-md" disabled={isLoading || !formData.name || (!formData.isNegotiable && !formData.price)}>
              {isLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
              {pricing ? '저장' : '추가'}
            </button>
          </div>
        </form>
      </div>

      {/* Template Select Modal */}
      {showTemplateSelect && (
        <PricingTemplateSelectModal
          productId={product.id}
          onSelect={handleLoadFromTemplate}
          onClose={() => setShowTemplateSelect(false)}
        />
      )}
    </div>,
    document.body
  )
}

// ============================================================================
// Pricing Template Select Modal (개별 옵션 선택 → 폼에 채움)
// ============================================================================
interface PricingTemplateSelectModalProps {
  onSelect: (option: CreateProductPricingRequest) => void
  onClose: () => void
  productId: number
}

function PricingTemplateSelectModal({ onSelect, onClose, productId }: PricingTemplateSelectModalProps) {
  const { data: templates = [], isLoading } = useQuery({
    queryKey: ['admin', 'pricing-templates'],
    queryFn: () => adminApi.getPricingTemplates(),
  })

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('ko-KR', { style: 'currency', currency: 'KRW' }).format(price)
  }

  const handleSelectOption = (opt: PricingOptionConfig) => {
    onSelect({
      productId,
      name: opt.name,
      pricingType: opt.pricingType,
      billingPeriodMonths: opt.billingPeriodMonths,
      price: opt.price,
      currency: opt.currency,
      originalPrice: opt.originalPrice ?? undefined,
      discountPercentage: opt.discountPercentage ?? undefined,
      maxSeats: opt.maxSeats,
      hasTrial: opt.hasTrial,
      trialDays: opt.trialDays,
      isDefault: opt.isDefault,
      displayOrder: opt.displayOrder,
      isNegotiable: opt.isNegotiable,
    })
    onClose()
  }

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-slate-200">
          <div>
            <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
              <FileDown className="w-5 h-5 text-blue-600" />
              템플릿에서 불러오기
            </h2>
            <p className="text-sm text-slate-500 mt-1">옵션을 선택하면 폼에 자동으로 채워집니다</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 text-slate-400 animate-spin" />
            </div>
          ) : templates.length === 0 ? (
            <div className="text-center py-12">
              <Copy className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">등록된 가격 템플릿이 없습니다</p>
            </div>
          ) : (
            <div className="space-y-4">
              {templates.map((template) => (
                <div key={template.id}>
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="text-sm font-semibold text-slate-700">{template.name}</h3>
                    {template.isSystemDefault && (
                      <span className="px-1.5 py-0.5 bg-blue-100 text-blue-700 text-[10px] font-medium rounded">
                        시스템
                      </span>
                    )}
                    {template.description && (
                      <span className="text-xs text-slate-400">{template.description}</span>
                    )}
                  </div>
                  <div className="space-y-1.5">
                    {template.pricingOptions.pricingOptions.map((opt, idx) => (
                      <button
                        key={idx}
                        onClick={() => handleSelectOption(opt)}
                        className="w-full text-left flex items-center justify-between border border-slate-200 rounded-lg px-4 py-2.5 hover:border-blue-300 hover:bg-blue-50/50 transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          <span className="font-medium text-sm text-slate-900">{opt.name}</span>
                          <span className="text-xs text-slate-500">
                            {PRICING_TYPE_LABELS[opt.pricingType]} · {billingPeriodLabel(opt.billingPeriodMonths)}
                          </span>
                          {opt.maxSeats > 1 && (
                            <span className="text-xs text-slate-500">{opt.maxSeats}석</span>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          {!opt.isNegotiable ? (
                            <span className="font-semibold text-sm text-slate-900">{formatPrice(opt.price)}</span>
                          ) : (
                            <span className="text-sm text-slate-500">별도 협의</span>
                          )}
                          {opt.isDefault && (
                            <Star className="w-3.5 h-3.5 text-amber-500 fill-amber-500" />
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// ============================================================================
// Save As Template Modal (개별 가격 옵션을 템플릿으로 저장)
// ============================================================================
interface SaveAsTemplateModalProps {
  pricing: ProductPricing
  onClose: () => void
  onSaved: () => void
}

function SaveAsTemplateModal({ pricing, onClose, onSaved }: SaveAsTemplateModalProps) {
  const [name, setName] = useState(pricing.name)
  const [description, setDescription] = useState('')

  const saveMutation = useMutation({
    mutationFn: (data: { name: string; description?: string }) =>
      adminApi.createPricingTemplate({
        name: data.name,
        description: data.description,
        pricingOptions: {
          version: '1.0',
          pricingOptions: [{
            name: pricing.name,
            pricingType: pricing.pricingType,
            billingPeriodMonths: pricing.billingPeriodMonths,
            price: pricing.price,
            currency: pricing.currency,
            originalPrice: pricing.originalPrice,
            discountPercentage: pricing.discountPercentage,
            maxSeats: pricing.maxSeats,
            hasTrial: pricing.hasTrial,
            trialDays: pricing.trialDays,
            isDefault: pricing.isDefault,
            displayOrder: pricing.displayOrder,
            isNegotiable: pricing.isNegotiable,
          }],
        },
      }),
    onSuccess: () => {
      toast.success('가격 옵션이 템플릿으로 저장되었습니다')
      onSaved()
      onClose()
    },
    onError: () => {
      toast.error('템플릿 저장에 실패했습니다')
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!name.trim()) return
    saveMutation.mutate({ name: name.trim(), description: description.trim() || undefined })
  }

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
        <div className="flex items-center justify-between p-6 border-b border-slate-200">
          <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
            <Save className="w-5 h-5 text-green-600" />
            템플릿으로 저장
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="bg-slate-50 rounded-lg p-3 text-sm text-slate-600">
            <span className="font-medium">{pricing.name}</span>
            {!pricing.isNegotiable && (
              <span className="ml-2">
                {new Intl.NumberFormat('ko-KR', { style: 'currency', currency: 'KRW' }).format(pricing.price)}
              </span>
            )}
            {pricing.isNegotiable && <span className="ml-2">별도 협의</span>}
          </div>
          <div>
            <label className="label">템플릿 이름 *</label>
            <input
              type="text"
              className="input"
              placeholder="예: 기본 구독 플랜, B2B 기업용"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              autoFocus
            />
          </div>
          <div>
            <label className="label">설명</label>
            <textarea
              className="input"
              rows={2}
              placeholder="이 템플릿에 대한 간단한 설명"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="btn-outline btn-md">
              취소
            </button>
            <button
              type="submit"
              disabled={!name.trim() || saveMutation.isPending}
              className="btn-primary btn-md"
            >
              {saveMutation.isPending ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              저장
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// ============================================================================
// Pricing Management Modal
// ============================================================================
interface PricingManagementModalProps {
  product: AdminProduct
  onClose: () => void
}

function PricingManagementModal({ product, onClose }: PricingManagementModalProps) {
  const queryClient = useQueryClient()
  const [editingPricing, setEditingPricing] = useState<ProductPricing | null>(null)
  const [isCreating, setIsCreating] = useState(false)
  const [savingAsPricing, setSavingAsPricing] = useState<ProductPricing | null>(null)

  const { data: pricings = [], isLoading } = useQuery({
    queryKey: ['admin', 'products', product.id, 'pricings'],
    queryFn: () => adminApi.getProductPricings(product.id),
  })

  const createPricingMutation = useMutation({
    mutationFn: (data: CreateProductPricingRequest) => adminApi.createProductPricing(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'products', product.id, 'pricings'] })
      queryClient.invalidateQueries({ queryKey: ['admin', 'products'] })
      setIsCreating(false)
    },
  })

  const updatePricingMutation = useMutation({
    mutationFn: ({ pricingId, data }: { pricingId: number; data: CreateProductPricingRequest }) =>
      adminApi.updateProductPricing(product.id, pricingId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'products', product.id, 'pricings'] })
      queryClient.invalidateQueries({ queryKey: ['admin', 'products'] })
      setEditingPricing(null)
    },
  })

  const deletePricingMutation = useMutation({
    mutationFn: (pricingId: number) => adminApi.deleteProductPricing(product.id, pricingId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'products', product.id, 'pricings'] })
      queryClient.invalidateQueries({ queryKey: ['admin', 'products'] })
    },
  })

  const handleDelete = (pricing: ProductPricing) => {
    if (confirm(`"${pricing.name}" 가격 옵션을 삭제하시겠습니까?`)) {
      deletePricingMutation.mutate(pricing.id)
    }
  }

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('ko-KR', { style: 'currency', currency: 'KRW' }).format(price)
  }

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-slate-200">
          <div>
            <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-green-600" />
              가격 관리
            </h2>
            <p className="text-sm text-slate-500 mt-1">{product.name}</p>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 text-slate-400 animate-spin" />
            </div>
          ) : pricings.length === 0 ? (
            <div className="text-center py-12">
              <DollarSign className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500 mb-4">등록된 가격 옵션이 없습니다</p>
              <button onClick={() => setIsCreating(true)} className="btn-primary btn-md">
                <Plus className="w-4 h-4 mr-2" />
                첫 가격 옵션 추가
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              {pricings.map((pricing) => (
                <div
                  key={pricing.id}
                  className={`border rounded-lg p-4 ${pricing.isDefault ? 'border-blue-300 bg-blue-50' : 'border-slate-200'
                    } ${!pricing.isActive ? 'opacity-60' : ''}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="font-semibold text-slate-900">{pricing.name}</h3>
                        {pricing.isDefault && (
                          <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-medium rounded-full">
                            추천
                          </span>
                        )}
                        {!pricing.isActive && (
                          <span className="px-2 py-0.5 bg-slate-100 text-slate-500 text-xs font-medium rounded-full">
                            비활성
                          </span>
                        )}
                        {pricing.hasTrial && (
                          <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded-full">
                            {pricing.trialDays}일 무료체험
                          </span>
                        )}
                      </div>
                      {!pricing.isNegotiable && (
                        <div className="flex items-baseline gap-2">
                          <span className="text-2xl font-bold text-slate-900">{formatPrice(pricing.price)}</span>
                          {pricing.originalPrice && pricing.originalPrice > pricing.price && (
                            <>
                              <span className="text-sm text-slate-400 line-through">{formatPrice(pricing.originalPrice)}</span>
                              <span className="text-sm font-medium text-red-500">
                                {pricing.discountPercentage || Math.round(((pricing.originalPrice - pricing.price) / pricing.originalPrice) * 100)}% 할인
                              </span>
                            </>
                          )}
                        </div>
                      )}
                      <div className="flex items-center gap-4 mt-2 text-sm text-slate-500">
                        <span>{PRICING_TYPE_LABELS[pricing.pricingType]}</span>
                        <span>{pricing.billingPeriodLabel}</span>
                        <span>최대 {pricing.maxSeats}명</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => setSavingAsPricing(pricing)}
                        className="p-2 hover:bg-slate-100 rounded-lg text-green-600"
                        title="템플릿으로 저장"
                      >
                        <Save className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => setEditingPricing(pricing)}
                        className="p-2 hover:bg-slate-100 rounded-lg text-slate-600"
                        title="수정"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(pricing)}
                        className="p-2 hover:bg-slate-100 rounded-lg text-red-500"
                        title="삭제"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="p-6 border-t border-slate-200 bg-slate-50 flex gap-3">
          <button onClick={() => setIsCreating(true)} className="btn-primary btn-md flex-1">
            <Plus className="w-4 h-4 mr-2" />
            새 가격 옵션 추가
          </button>
          <button onClick={onClose} className="btn-md px-6 bg-slate-700 text-white rounded-lg hover:bg-slate-800 transition-colors">
            확인
          </button>
        </div>
      </div>

      {/* Pricing Form Modal */}
      {(isCreating || editingPricing) && (
        <PricingFormModal
          product={product}
          pricing={editingPricing}
          onClose={() => {
            setIsCreating(false)
            setEditingPricing(null)
          }}
          onSave={(data) => {
            if (editingPricing) {
              updatePricingMutation.mutate({ pricingId: editingPricing.id, data })
            } else {
              createPricingMutation.mutate(data)
            }
          }}
          isLoading={createPricingMutation.isPending || updatePricingMutation.isPending}
        />
      )}

      {/* Save As Template Modal */}
      {savingAsPricing && (
        <SaveAsTemplateModal
          pricing={savingAsPricing}
          onClose={() => setSavingAsPricing(null)}
          onSaved={() => {
            queryClient.invalidateQueries({ queryKey: ['admin', 'pricing-templates'] })
          }}
        />
      )}
    </div>,
    document.body
  )
}

interface PurchaseSubscriptionModalProps {
  product: AdminProduct
  organization: ProductOrganization
  onClose: () => void
  onSuccess: () => void
}

function PurchaseSubscriptionModal({ product, organization, onClose, onSuccess }: PurchaseSubscriptionModalProps) {
  const queryClient = useQueryClient()
  const [selectedPricingId, setSelectedPricingId] = useState<number | null>(null)
  const [startDate, setStartDate] = useState<string>(() => new Date().toISOString().split('T')[0])
  const [ownerUserId, setOwnerUserId] = useState<number | null>(null)

  const { data: pricings = [], isLoading: isPricingLoading } = useQuery({
    queryKey: ['admin', 'products', product.id, 'pricings', 'purchase-modal'],
    queryFn: () => adminApi.getProductPricings(product.id),
  })

  const { data: members = [], isLoading: isMembersLoading } = useQuery({
    queryKey: ['admin', 'organizations', organization.organizationId, 'members'],
    queryFn: () => adminApi.getOrganizationMembers(organization.organizationId),
  })

  const adminRoles = new Set(['owner', 'admin', 'admin_learner'])
  const representativeBuyers = members.filter((member) =>
    adminRoles.has((member.role || '').toLowerCase())
  )

  useEffect(() => {
    if (pricings.length > 0 && selectedPricingId == null) {
      const defaultPricing = pricings.find((p) => p.isDefault) ?? pricings[0]
      setSelectedPricingId(defaultPricing?.id ?? null)
    }
  }, [pricings, selectedPricingId])

  useEffect(() => {
    if (representativeBuyers.length > 0 && ownerUserId == null) {
      setOwnerUserId(representativeBuyers[0].userId)
    }
  }, [representativeBuyers, ownerUserId])

  const createSubscriptionMutation = useMutation({
    mutationFn: (payload: {
      orderId: number
      productId: number
      pricingId: number
      ownerUserId: number
      organizationId: number
      subscriptionType: SubscriptionType
      maxSeats: number
      validFrom: string
      validUntil?: string
      autoRenew: boolean
    }) => adminApi.createSubscription(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'subscriptions'] })
      queryClient.invalidateQueries({ queryKey: ['admin', 'products', product.id, 'organizations'] })
      onSuccess()
    },
  })

  const formatPrice = (price: number) =>
    new Intl.NumberFormat('ko-KR', { style: 'currency', currency: 'KRW' }).format(price)

  const addMonths = (date: Date, months: number): Date => {
    const next = new Date(date)
    next.setMonth(next.getMonth() + months)
    return next
  }

  const toLocalDateTime = (date: Date): string => {
    const pad = (n: number) => String(n).padStart(2, '0')
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T00:00:00`
  }

  const resolveSubscriptionType = (pricing: ProductPricing): SubscriptionType => {
    if (pricing.pricingType === 'one_time') return 'one_time'
    if (pricing.billingPeriodMonths === 3) return 'quarterly'
    if (pricing.billingPeriodMonths === 12) return 'yearly'
    return 'monthly'
  }

  const resolveValidUntil = (pricing: ProductPricing, start: Date): string | undefined => {
    if (pricing.billingPeriodMonths === 0) return undefined // 평생
    return toLocalDateTime(addMonths(start, pricing.billingPeriodMonths))
  }

  // 선택된 가격 옵션에서 파생 값 자동 계산
  const selectedPricing = pricings.find((p) => p.id === selectedPricingId) ?? null
  const autoMaxSeats = selectedPricing?.maxSeats ?? 0
  const autoEndDate = (() => {
    if (!selectedPricing || !startDate) return null
    const start = new Date(`${startDate}T00:00:00`)
    if (selectedPricing.billingPeriodMonths === 0) return null
    return addMonths(start, selectedPricing.billingPeriodMonths)
  })()

  const handlePurchase = () => {
    if (!selectedPricingId || !selectedPricing || !ownerUserId || !startDate) return

    const buyer = representativeBuyers.find(m => m.userId === ownerUserId)
    const seatsText = autoMaxSeats === 0 ? '무제한' : `${autoMaxSeats.toLocaleString()}명`
    const periodText = selectedPricing.billingPeriodMonths === 0
      ? '무기한'
      : `${selectedPricing.billingPeriodMonths}개월`
    const priceText = selectedPricing.isNegotiable ? '별도 협의' : formatPrice(selectedPricing.price)

    const confirmed = confirm(
      `다음 내용으로 구매를 진행합니다.\n\n` +
      `상품: ${product.name}\n` +
      `회사: ${organization.organizationDisplayName || organization.organizationName}\n` +
      `가격 옵션: ${selectedPricing.name}\n` +
      `좌석수: ${seatsText}\n` +
      `기간: ${periodText}\n` +
      `금액: ${priceText}\n` +
      `대표 구매자: ${buyer ? `${buyer.fullName} (${buyer.email})` : '-'}\n\n` +
      `진행하시겠습니까?`
    )
    if (!confirmed) return

    const validFromDate = new Date(`${startDate}T00:00:00`)

    createSubscriptionMutation.mutate({
      orderId: Date.now(),
      productId: product.id,
      pricingId: selectedPricing.id,
      ownerUserId,
      organizationId: organization.organizationId,
      subscriptionType: resolveSubscriptionType(selectedPricing),
      maxSeats: autoMaxSeats,
      validFrom: toLocalDateTime(validFromDate),
      validUntil: resolveValidUntil(selectedPricing, validFromDate),
      autoRenew: true,
    })
  }

  return createPortal(
    <div className="fixed inset-0 z-[9999] overflow-y-auto">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
          {/* 헤더 */}
          <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">{product.name}</h2>
              <p className="text-sm text-gray-500 mt-1">구매 회사: {organization.organizationDisplayName || organization.organizationName}</p>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          <div className="p-6 space-y-6">
            {/* 가격 옵션 선택 (라디오 카드) */}
            <div>
              <h4 className="text-sm font-semibold text-gray-700 mb-3">가격 옵션 선택</h4>
              {isPricingLoading ? (
                <div className="flex items-center gap-2 text-slate-500">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>가격 옵션 로딩 중...</span>
                </div>
              ) : (
                <div className="space-y-3">
                  {pricings.map((pricing) => (
                    <label
                      key={pricing.id}
                      className={`block border-2 rounded-lg p-4 cursor-pointer transition-all ${
                        selectedPricingId === pricing.id
                          ? 'border-primary-600 bg-primary-50/30 ring-1 ring-primary-200'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <input
                          type="radio"
                          name="pricing"
                          checked={selectedPricingId === pricing.id}
                          onChange={() => setSelectedPricingId(pricing.id)}
                          className="mt-1"
                        />
                        <div className="flex-1">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <span className="font-semibold text-gray-900">{pricing.name}</span>
                              {pricing.isDefault && (
                                <span className="px-1.5 py-0.5 text-[10px] font-semibold bg-primary-100 text-primary-700 rounded">
                                  기본
                                </span>
                              )}
                              <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                                pricing.pricingType === 'one_time'
                                  ? 'bg-blue-50 text-blue-700'
                                  : 'bg-green-50 text-green-700'
                              }`}>
                                {pricing.pricingType === 'one_time' ? '일회성' : '구독'}
                              </span>
                            </div>
                            {!pricing.isNegotiable ? (
                              <div className="text-right">
                                {pricing.originalPrice && pricing.originalPrice > pricing.price && (
                                  <span className="block text-sm text-gray-400 line-through">
                                    {formatPrice(pricing.originalPrice)}
                                  </span>
                                )}
                                <span className="text-xl font-bold text-primary-600">
                                  {formatPrice(pricing.price)}
                                </span>
                              </div>
                            ) : (
                              <span className="text-lg font-bold text-gray-700">별도 협의</span>
                            )}
                          </div>

                          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                            <div className="bg-white rounded-md p-2 border border-gray-100">
                              <span className="block text-[10px] text-gray-500 mb-0.5">결제 주기</span>
                              <span className="font-medium text-gray-900 text-xs">
                                {pricing.billingPeriodMonths
                                  ? `${pricing.billingPeriodMonths}개월`
                                  : '평생'}
                              </span>
                            </div>
                            <div className="bg-white rounded-md p-2 border border-gray-100">
                              <span className="block text-[10px] text-gray-500 mb-0.5">최대 좌석</span>
                              <span className="font-medium text-gray-900 text-xs">
                                {pricing.maxSeats === 0 ? '무제한' : `${pricing.maxSeats.toLocaleString()}명`}
                              </span>
                            </div>
                            <div className="bg-white rounded-md p-2 border border-gray-100">
                              <span className="block text-[10px] text-gray-500 mb-0.5">무료 체험</span>
                              <span className="font-medium text-gray-900 text-xs">
                                {pricing.hasTrial && pricing.trialDays
                                  ? <span className="text-green-600">{pricing.trialDays}일</span>
                                  : '없음'}
                              </span>
                            </div>
                            {pricing.discountPercentage && pricing.discountPercentage > 0 ? (
                              <div className="bg-red-50 rounded-md p-2 border border-red-100">
                                <span className="block text-[10px] text-red-500 mb-0.5">할인</span>
                                <span className="font-medium text-red-700 text-xs">
                                  {pricing.discountPercentage}% 할인
                                </span>
                              </div>
                            ) : (
                              <div className="bg-white rounded-md p-2 border border-gray-100">
                                <span className="block text-[10px] text-gray-500 mb-0.5">할인</span>
                                <span className="font-medium text-gray-400 text-xs">-</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
              )}
            </div>

            {/* 유효기간 + 요약 */}
            <div className="bg-gray-50 rounded-lg p-5 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  상품 유효 기간
                </label>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">시작일</label>
                    <input
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      min={new Date().toISOString().split('T')[0]}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">종료일 (선택된 가격 옵션에 따라 정해짐)</label>
                    <div className="w-full px-4 py-2 bg-gray-100 border border-gray-200 rounded-lg text-gray-700">
                      {autoEndDate
                        ? autoEndDate.toLocaleDateString('ko-KR', { year: 'numeric', month: 'long', day: 'numeric' })
                        : '무기한'}
                    </div>
                  </div>
                </div>
              </div>

              {/* 선택 요약 */}
              {selectedPricing && (
                <div className="bg-white rounded-lg p-4 border border-gray-200">
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-500">가격 옵션</span>
                      <span className="font-medium text-gray-900">
                        {selectedPricing.name}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">License (좌석수)</span>
                      <span className="font-medium text-gray-900">
                        {autoMaxSeats === 0 ? '무제한' : `${autoMaxSeats.toLocaleString()}명`}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">결제 금액</span>
                      <span className="font-bold text-primary-600">
                        {selectedPricing.isNegotiable ? '별도 협의' : formatPrice(selectedPricing.price)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">시작일</span>
                      <span className="font-medium text-gray-900">
                        {startDate ? new Date(`${startDate}T00:00:00`).toLocaleDateString('ko-KR', {
                          year: 'numeric', month: 'long', day: 'numeric',
                        }) : '-'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">종료일</span>
                      <span className="font-medium text-gray-900">
                        {autoEndDate
                          ? autoEndDate.toLocaleDateString('ko-KR', { year: 'numeric', month: 'long', day: 'numeric' })
                          : '무기한'}
                      </span>
                    </div>
                    <div className="flex justify-between pt-2 border-t border-gray-100 mt-2">
                      <span className="text-gray-500">대표 구매자</span>
                      <span className="font-medium text-gray-900">
                        {(() => {
                          const buyer = representativeBuyers.find(m => m.userId === ownerUserId)
                          return buyer ? `${buyer.fullName} (${buyer.email})` : '-'
                        })()}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* 대표 구매자 선택 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">대표구매자 (회사 Admin)</label>
              <select
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                value={ownerUserId ?? ''}
                onChange={(e) => setOwnerUserId(Number(e.target.value))}
                disabled={isMembersLoading || representativeBuyers.length === 0}
              >
                {representativeBuyers.length === 0 ? (
                  <option value="">회사 Admin 권한 사용자가 없습니다</option>
                ) : (
                  representativeBuyers.map((member) => (
                    <option key={member.userId} value={member.userId}>
                      {member.fullName} ({member.email})
                    </option>
                  ))
                )}
              </select>
              {representativeBuyers.length === 0 && (
                <p className="mt-1 text-xs text-red-600">
                  이 조직에 회사 Admin 권한(owner/admin/admin_learner) 사용자가 없어 구매를 진행할 수 없습니다.
                </p>
              )}
            </div>

            {/* 구매하기 버튼 */}
            <button
              type="button"
              onClick={handlePurchase}
              disabled={!selectedPricingId || !ownerUserId || representativeBuyers.length === 0 || createSubscriptionMutation.isPending}
              className={`w-full py-3 px-6 rounded-lg font-semibold text-lg transition-colors flex items-center justify-center ${
                !selectedPricingId || !ownerUserId || representativeBuyers.length === 0 || createSubscriptionMutation.isPending
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-primary-600 text-white hover:bg-primary-700'
              }`}
            >
              {createSubscriptionMutation.isPending ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  처리 중...
                </>
              ) : (
                <>
                  <DollarSign className="w-5 h-5 mr-2" />
                  구매하기
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  )
}

// ============================================================================
// Open to Org Modal
// ============================================================================
interface OpenToOrgModalProps {
  productIds: number[]
  onClose: () => void
  onSuccess: () => void
}

function OpenToOrgModal({ productIds, onClose, onSuccess }: OpenToOrgModalProps) {
  const [selectedOrgs, setSelectedOrgs] = useState<number[]>([])
  const [validFrom, setValidFrom] = useState(new Date().toISOString().split('T')[0])
  const [validUntil, setValidUntil] = useState<string>('')

  const { data: organizations = [], isLoading: isLoadingOrgs } = useQuery({
    queryKey: ['admin', 'organizations'],
    queryFn: () => adminApi.getAllOrganizations(),
  })

  const openToOrgMutation = useMutation({
    mutationFn: async () => {
      // 각 상품에 대해 조직들을 연결
      const results = await Promise.all(
        productIds.map(productId =>
          adminApi.openProductToOrganizations(productId, {
            organizationIds: selectedOrgs,
            validFrom,
            validUntil: validUntil || undefined,
          })
        )
      )
      return results
    },
    onSuccess: () => {
      onSuccess()
      onClose()
    },
  })

  const toggleOrg = (orgId: number) => {
    setSelectedOrgs(prev =>
      prev.includes(orgId)
        ? prev.filter(id => id !== orgId)
        : [...prev, orgId]
    )
  }

  const toggleAll = () => {
    if (selectedOrgs.length === organizations.length) {
      setSelectedOrgs([])
    } else {
      setSelectedOrgs(organizations.map(o => o.id))
    }
  }

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-slate-200">
          <div>
            <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
              <Building2 className="w-5 h-5 text-blue-600" />
              Open to Org
            </h2>
            <p className="text-sm text-slate-500 mt-1">
              {productIds.length}개 상품을 조직에 공개합니다
            </p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* 주의 사항 */}
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <div className="flex gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-amber-800">
                <p className="font-medium mb-1">상품 상태를 확인하세요!</p>
                <p className="text-amber-700">
                  <span className="font-semibold">"초안(draft)"</span> 상태의 상품은 조직에 공개해도 
                  Company Admin의 구매 가능한 상품 목록에 표시되지 않습니다. 
                  공개 전에 상품 상태를 <span className="font-semibold">"활성(active)"</span>으로 
                  변경해주세요.
                </p>
              </div>
            </div>
          </div>

          {/* 유효 기간 설정 */}
          <div className="bg-slate-50 p-4 rounded-lg space-y-4">
            <h3 className="font-semibold text-slate-900">유효 기간</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="label">시작일 *</label>
                <input
                  type="date"
                  className="input"
                  value={validFrom}
                  onChange={(e) => setValidFrom(e.target.value)}
                  required
                />
              </div>
              <div>
                <label className="label">종료일 (선택)</label>
                <input
                  type="date"
                  className="input"
                  value={validUntil}
                  onChange={(e) => setValidUntil(e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* 조직 선택 */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-slate-900">조직 선택</h3>
              <button
                onClick={toggleAll}
                className="text-sm text-blue-600 hover:text-blue-700"
              >
                {selectedOrgs.length === organizations.length ? '전체 해제' : '전체 선택'}
              </button>
            </div>

            {isLoadingOrgs ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 text-slate-400 animate-spin" />
              </div>
            ) : organizations.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                등록된 조직이 없습니다
              </div>
            ) : (
              <div className="space-y-2 max-h-[300px] overflow-y-auto border border-slate-200 rounded-lg p-3">
                {organizations.map((org) => (
                  <label
                    key={org.id}
                    className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors ${
                      selectedOrgs.includes(org.id)
                        ? 'bg-blue-50 border border-blue-200'
                        : 'bg-white border border-slate-100 hover:bg-slate-50'
                    }`}
                  >
                    <input
                      type="checkbox"
                      className="w-5 h-5 rounded border-slate-300"
                      checked={selectedOrgs.includes(org.id)}
                      onChange={() => toggleOrg(org.id)}
                    />
                    <div className="flex-1">
                      <p className="font-medium text-slate-900">{org.displayName || org.name}</p>
                      <p className="text-xs text-slate-500">
                        {org.subdomain ? `${org.subdomain}.quebon.tv` : org.organizationType}
                      </p>
                    </div>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                      org.organizationType === 'company' ? 'bg-blue-100 text-blue-700' :
                      org.organizationType === 'academy' ? 'bg-purple-100 text-purple-700' :
                      'bg-slate-100 text-slate-600'
                    }`}>
                      {org.organizationType}
                    </span>
                  </label>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="p-6 border-t border-slate-200 bg-slate-50">
          <div className="flex justify-end gap-3">
            <button onClick={onClose} className="btn-secondary btn-md">
              취소
            </button>
            <button
              onClick={() => openToOrgMutation.mutate()}
              className="btn-primary btn-md"
              disabled={selectedOrgs.length === 0 || openToOrgMutation.isPending}
            >
              {openToOrgMutation.isPending ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Building2 className="w-4 h-4 mr-2" />
              )}
              {selectedOrgs.length}개 조직에 공개
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  )
}

// ============================================================================
// Add Organization to Product Modal
// ============================================================================
interface AddOrganizationToProductModalProps {
  product: AdminProduct
  existingOrganizationIds: number[]
  onClose: () => void
  onSuccess: () => void
}

function AddOrganizationToProductModal({ product, existingOrganizationIds, onClose, onSuccess }: AddOrganizationToProductModalProps) {
  const [selectedOrgs, setSelectedOrgs] = useState<number[]>([])
  const [validFrom, setValidFrom] = useState(new Date().toISOString().split('T')[0])
  const [validUntil, setValidUntil] = useState<string>('')

  const { data: organizations = [], isLoading: isLoadingOrgs } = useQuery({
    queryKey: ['admin', 'organizations'],
    queryFn: () => adminApi.getAllOrganizations(),
  })

  // 이미 공개된 조직을 제외한 조직 목록
  const availableOrganizations = organizations.filter(
    org => !existingOrganizationIds.includes(org.id)
  )

  const addToOrgMutation = useMutation({
    mutationFn: async () => {
      return adminApi.openProductToOrganizations(product.id, {
        organizationIds: selectedOrgs,
        validFrom,
        validUntil: validUntil || undefined,
      })
    },
    onSuccess: () => {
      onSuccess()
      onClose()
    },
  })

  const toggleOrg = (orgId: number) => {
    setSelectedOrgs(prev =>
      prev.includes(orgId)
        ? prev.filter(id => id !== orgId)
        : [...prev, orgId]
    )
  }

  const toggleAll = () => {
    if (selectedOrgs.length === availableOrganizations.length) {
      setSelectedOrgs([])
    } else {
      setSelectedOrgs(availableOrganizations.map(o => o.id))
    }
  }

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-slate-200">
          <div>
            <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
              <Building2 className="w-5 h-5 text-blue-600" />
              조직에 공개
            </h2>
            <p className="text-sm text-slate-500 mt-1">{product.name}</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* 주의 사항 */}
          {product.status === 'draft' && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <div className="flex gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-amber-800">
                  <p className="font-medium mb-1">상품 상태를 확인하세요!</p>
                  <p className="text-amber-700">
                    현재 상품이 <span className="font-semibold">"초안(draft)"</span> 상태입니다.
                    Company Admin의 구매 가능한 상품 목록에 표시되려면
                    <span className="font-semibold">"활성(active)"</span>으로 변경해주세요.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* 유효 기간 설정 */}
          <div className="bg-slate-50 p-4 rounded-lg space-y-4">
            <h3 className="font-semibold text-slate-900">유효 기간</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="label">시작일 *</label>
                <input
                  type="date"
                  className="input"
                  value={validFrom}
                  onChange={(e) => setValidFrom(e.target.value)}
                  required
                />
              </div>
              <div>
                <label className="label">종료일 (선택)</label>
                <input
                  type="date"
                  className="input"
                  value={validUntil}
                  onChange={(e) => setValidUntil(e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* 조직 선택 */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-slate-900">조직 선택</h3>
              {availableOrganizations.length > 0 && (
                <button
                  onClick={toggleAll}
                  className="text-sm text-blue-600 hover:text-blue-700"
                >
                  {selectedOrgs.length === availableOrganizations.length ? '전체 해제' : '전체 선택'}
                </button>
              )}
            </div>

            {isLoadingOrgs ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 text-slate-400 animate-spin" />
              </div>
            ) : availableOrganizations.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                {organizations.length === existingOrganizationIds.length ? (
                  <p>모든 조직에 이미 공개되어 있습니다</p>
                ) : (
                  <p>등록된 조직이 없습니다</p>
                )}
              </div>
            ) : (
              <div className="space-y-2 max-h-[300px] overflow-y-auto border border-slate-200 rounded-lg p-3">
                {availableOrganizations.map((org) => (
                  <label
                    key={org.id}
                    className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors ${
                      selectedOrgs.includes(org.id)
                        ? 'bg-blue-50 border border-blue-200'
                        : 'bg-white border border-slate-100 hover:bg-slate-50'
                    }`}
                  >
                    <input
                      type="checkbox"
                      className="w-5 h-5 rounded border-slate-300"
                      checked={selectedOrgs.includes(org.id)}
                      onChange={() => toggleOrg(org.id)}
                    />
                    <div className="flex-1">
                      <p className="font-medium text-slate-900">{org.displayName || org.name}</p>
                      <p className="text-xs text-slate-500">
                        {org.subdomain ? `${org.subdomain}.quebon.tv` : org.organizationType}
                      </p>
                    </div>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                      org.organizationType === 'company' ? 'bg-blue-100 text-blue-700' :
                      org.organizationType === 'academy' ? 'bg-purple-100 text-purple-700' :
                      'bg-slate-100 text-slate-600'
                    }`}>
                      {org.organizationType}
                    </span>
                  </label>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="p-6 border-t border-slate-200 bg-slate-50">
          <div className="flex justify-end gap-3">
            <button onClick={onClose} className="btn-secondary btn-md">
              취소
            </button>
            <button
              onClick={() => addToOrgMutation.mutate()}
              className="btn-primary btn-md"
              disabled={selectedOrgs.length === 0 || addToOrgMutation.isPending}
            >
              {addToOrgMutation.isPending ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Plus className="w-4 h-4 mr-2" />
              )}
              {selectedOrgs.length}개 조직에 공개
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  )
}

// ============================================================================
// Product Config Cell (학습진행 컬럼용 - 설정 여부 표시 및 모달 오픈)
// ============================================================================
interface ProductConfigCellProps {
  product: AdminProduct
  onOpenConfig: (product: AdminProduct) => void
}

function ProductConfigCell({ product, onOpenConfig }: ProductConfigCellProps) {
  const { data: config, isLoading } = useQuery({
    queryKey: ['configuration', 'product', product.id],
    queryFn: () => configApi.getProductConfiguration(product.id),
    staleTime: 30 * 1000,
  })
  const hasConfig = !!config || !!product.learningConfigMode
  if (isLoading) {
    return (
      <td className="px-3 py-4 text-center">
        <span className="text-xs text-slate-400">...</span>
      </td>
    )
  }
  return (
    <td className="px-3 py-4">
      <button
        type="button"
        onClick={() => onOpenConfig(product)}
        className={
          hasConfig
            ? 'px-2 py-1 text-xs font-medium text-slate-600 bg-slate-50 hover:bg-slate-100 rounded border border-slate-200 transition-colors'
            : 'px-2 py-1 text-xs font-medium text-red-600 bg-red-50 hover:bg-red-100 rounded border border-red-200 transition-colors'
        }
      >
        {hasConfig ? (<>진행<br/>보기</>) : (<>진행<br/>설정</>)}
      </button>
    </td>
  )
}

// ============================================================================
// Product Detail Panel (Expandable)
// ============================================================================
interface ProductDetailPanelProps {
  product: AdminProduct
  onEdit: (product: AdminProduct) => void
  onDelete: (product: AdminProduct) => void
  onPricingManage: (product: AdminProduct) => void
}

function ProductDetailPanel({ product, onEdit, onDelete, onPricingManage }: ProductDetailPanelProps) {
  const queryClient = useQueryClient()
  const [showConfigEditModal, setShowConfigEditModal] = useState(false)
  const [showAddOrgModal, setShowAddOrgModal] = useState(false)
  const [purchaseOrg, setPurchaseOrg] = useState<ProductOrganization | null>(null)
  const [deleteStep, setDeleteStep] = useState<0 | 1 | 2 | 3 | 4>(0) // 0=닫힘, 1=구독중 경고, 2=최종 확인, 3=간단 확인, 4=공개조직 있음 경고
  const [missingRequirements, setMissingRequirements] = useState<string[]>([]) // 조직 추가 시 누락 설정

  const { data: organizations = [], isLoading } = useQuery({
    queryKey: ['admin', 'products', product.id, 'organizations'],
    queryFn: () => adminApi.getProductOrganizations(product.id),
  })
  const visibleOrganizations = organizations.filter((org) => org.isActive)

  // 조직 공개 취소 mutation
  const closeFromOrgMutation = useMutation({
    mutationFn: (organizationId: number) =>
      adminApi.closeProductFromOrganization(product.id, organizationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'products', product.id, 'organizations'] })
    },
  })

  // 학습진행 설정 편집 허용 토글 mutation
  const toggleConfigEditMutation = useMutation({
    mutationFn: (allowConfigEdit: boolean) =>
      adminApi.updateProduct(product.id, { allowConfigEdit }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'products'] })
    },
  })

  const handleCloseFromOrg = (org: ProductOrganization) => {
    if (confirm(`"${org.organizationDisplayName || org.organizationName}" 조직에서 이 상품의 공개를 취소하시겠습니까?`)) {
      closeFromOrgMutation.mutate(org.organizationId)
    }
  }

  // 상품 Configuration 조회
  const { data: productConfiguration, isLoading: isProductConfigLoading } = useQuery({
    queryKey: ['configuration', 'product', product.id],
    queryFn: () => configApi.getProductConfiguration(product.id),
    staleTime: 30 * 1000,
  })

  // 커리큘럼 Configuration 조회 — 비활성화 (백엔드에 curriculum endpoint 없음)
  // v2에서는 ProductModeSelector를 통해 설정
  const curriculumConfiguration = null
  const isCurriculumConfigLoading = false

  // Configuration 저장 mutation
  const saveConfigMutation = useMutation({
    mutationFn: (config: LessonConfiguration) =>
      configApi.setProductConfiguration(product.id, config),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['configuration', 'product', product.id] })
      setShowConfigEditModal(false)
    },
    onError: (error) => {
      console.error('Configuration 저장 실패:', error)
      alert('Configuration 저장에 실패했습니다.')
    },
  })

  const handleSaveConfig = async (config: LessonConfiguration) => {
    await saveConfigMutation.mutateAsync(config)
  }

  const formatPrice = (price: number) =>
    new Intl.NumberFormat('ko-KR').format(price)

  // 로딩 상태 결합
  const isConfigLoading = isProductConfigLoading || (!productConfiguration && isCurriculumConfigLoading)

  // 표시할 configuration과 소스 결정
  const displayConfiguration = productConfiguration ?? curriculumConfiguration ?? null

  return (
    <>
      <tr className="bg-slate-50">
        <td colSpan={7} className="px-3 py-4">
          <div className="space-y-4 ml-40">

            {/* 공개된 조직 목록 */}
            <div>
              <div className="flex items-center gap-3 mb-2 -ml-20">
                <h4 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                  <Building2 className="w-4 h-4" />
                  공개된 조직 ({visibleOrganizations.length})
                </h4>
                <button
                  onClick={() => {
                    // 필수 설정 검증
                    const missing: string[] = []
                    const hasContent = product.isBundle
                      ? (product.bundleContents && product.bundleContents.length > 0)
                      : !!product.linkedContentId
                    if (!hasContent) missing.push('연결된 컨텐츠')
                    const hasConfig = displayConfiguration || !!product.learningConfigMode
                    if (!hasConfig) missing.push('학습진행 설정')
                    if (!product.pricings || product.pricings.length === 0) missing.push('가격 설정')

                    if (missing.length > 0) {
                      setMissingRequirements(missing)
                    } else {
                      setShowAddOrgModal(true)
                    }
                  }}
                  className="px-2 py-1 text-xs font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded border border-blue-200 transition-colors flex items-center gap-1"
                >
                  <Plus className="w-3 h-3" />
                  추가
                </button>
              </div>
              {isLoading ? (
                <div className="flex items-center gap-2 text-sm text-slate-500">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  로딩 중...
                </div>
              ) : visibleOrganizations.length === 0 ? (
                <p className="text-sm text-slate-500">공개된 조직이 없습니다</p>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {visibleOrganizations.map((org) => (
                    <div
                      key={org.organizationId}
                      className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium bg-slate-100 text-slate-700 border border-slate-200"
                    >
                      <span>{org.organizationDisplayName || org.organizationName}</span>
                      {org.hasActiveSubscription ? (
                        <span className="px-2 py-0.5 text-xs font-medium text-green-700 bg-green-100 rounded border border-green-200">
                          구독중
                        </span>
                      ) : (
                        <>
                          <button
                            onClick={() => setPurchaseOrg(org)}
                            className="px-2 py-0.5 text-xs font-medium text-red-700 bg-red-50 hover:bg-red-100 rounded border border-red-200 transition-colors"
                          >
                            직접 구매 해주기
                          </button>
                          <button
                            onClick={() => handleCloseFromOrg(org)}
                            disabled={closeFromOrgMutation.isPending}
                            className="px-2 py-0.5 text-xs font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 rounded border border-blue-200 transition-colors disabled:opacity-50"
                          >
                            {closeFromOrgMutation.isPending ? '...' : '공개취소'}
                          </button>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Bundle 콘텐츠 목록 */}
            {product.isBundle && (
              <div>
                <h4 className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-2 -ml-20">
                  <Layers className="w-4 h-4 text-purple-600" />
                  Bundle 콘텐츠 ({product.bundleContents?.length ?? 0})
                </h4>
                {product.bundleContents && product.bundleContents.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {product.bundleContents.map((content, index) => (
                      <div
                        key={content.contentId}
                        className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium bg-purple-50 text-purple-700 border border-purple-200"
                      >
                        <span className="text-xs text-purple-500">#{index + 1}</span>
                        <span>{content.contentTitle || `Content #${content.contentId}`}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-slate-500">등록된 콘텐츠가 없습니다. 상품 수정에서 콘텐츠를 추가하세요.</p>
                )}
              </div>
            )}

            {/* 가격 정보 */}
            <div>
              <h4 className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-2 -ml-20">
                <DollarSign className="w-4 h-4" />
                가격 정보
                <button
                  onClick={() => onPricingManage(product)}
                  className="px-2 py-0.5 text-xs font-medium text-green-700 bg-green-50 hover:bg-green-100 rounded border border-green-200 transition-colors"
                >
                  가격 관리
                </button>
              </h4>
              {product.pricings && product.pricings.length > 0 ? (
                <div className="flex flex-wrap gap-3">
                  {product.pricings.map((pricing) => (
                    <div
                      key={pricing.id}
                      className={`px-3 py-2 rounded-lg border ${
                        pricing.isDefault ? 'border-blue-300 bg-blue-50' : 'border-slate-200 bg-white'
                      }`}
                    >
                      <div className="font-medium text-slate-900">{pricing.name}</div>
                      <div className="text-xs text-slate-500">
                        {pricing.isNegotiable ? (
                          '별도 협의'
                        ) : (
                          `₩${formatPrice(pricing.price)}/${pricing.billingPeriodLabel}`
                        )}
                        {pricing.isDefault && ' · 추천'}
                        {pricing.discountPercentage && ` · ${pricing.discountPercentage}%↓`}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-slate-400">등록된 가격이 없습니다</p>
              )}
            </div>

            {/* Configuration 섹션 */}
            <div className="pt-4 border-t border-slate-200">
              {/* v2 Configuration 모드 선택 (기본) */}
              <ProductModeSelector
                productId={product.id}
                currentMode={product.learningConfigMode}
                currentTemplateId={product.learningConfigTemplateId}
              />

              {/* B2B 설정 편집 허용 토글 */}
              <div className="mt-3 flex items-center justify-between px-3 py-2 bg-slate-50 rounded-lg border border-slate-200">
                <div>
                  <span className="text-xs font-medium text-slate-700">B2B 설정 편집 허용</span>
                  <p className="text-xs text-slate-500">B2B Admin이 클래스별 학습진행 설정을 편집할 수 있도록 허용</p>
                </div>
                <button
                  onClick={() => toggleConfigEditMutation.mutate(!product.allowConfigEdit)}
                  disabled={toggleConfigEditMutation.isPending}
                  className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                    product.allowConfigEdit ? 'bg-green-500' : 'bg-slate-300'
                  } ${toggleConfigEditMutation.isPending ? 'opacity-50' : ''}`}
                >
                  <span
                    className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
                      product.allowConfigEdit ? 'translate-x-4' : 'translate-x-0.5'
                    }`}
                  />
                </button>
              </div>

              {/* v1 기존 설정 (있는 경우 표시 + 편집 링크) */}
              {displayConfiguration && (
                <div className="mt-3 pt-3 border-t border-slate-200">
                  <ConfigurationSummary
                    configuration={displayConfiguration}
                    onEdit={() => setShowConfigEditModal(true)}
                    isLoading={isConfigLoading}
                    titleClassName="-ml-20"
                  />
                </div>
              )}
              {!displayConfiguration && (
                <button
                  onClick={() => setShowConfigEditModal(true)}
                  className="mt-3 text-xs text-slate-400 hover:text-slate-600 underline"
                >
                  v1 기존 설정으로 편집
                </button>
              )}
            </div>

          </div>
          {/* 기본 정보 수정 / 상품 제거 (섹션 제목과 왼쪽 정렬) */}
          <div className="pt-4 mt-4 border-t border-slate-200 flex flex-wrap items-center gap-3 ml-20">
            <button
              type="button"
              onClick={() => onEdit(product)}
              className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-lg border border-slate-200 transition-colors"
            >
              <Edit2 className="w-4 h-4" />
              기본 정보 수정하기
            </button>
            <button
              type="button"
              onClick={() => {
                const subscribedOrgs = visibleOrganizations.filter(org => org.hasActiveSubscription)
                if (subscribedOrgs.length > 0) {
                  setDeleteStep(1)
                } else if (visibleOrganizations.length > 0) {
                  setDeleteStep(4)
                } else {
                  setDeleteStep(3)
                }
              }}
              className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-red-600 bg-red-50 hover:bg-red-100 rounded-lg border border-red-200 transition-colors"
            >
              <Trash2 className="w-4 h-4" />
              이 상품 제거하기
            </button>
          </div>
        </td>
      </tr>

      {/* Configuration 편집 모달 */}
      {showConfigEditModal && (
        <ConfigurationEditModal
          targetType="product"
          targetId={product.id}
          targetName={product.name}
          configuration={productConfiguration ?? curriculumConfiguration ?? null}
          onClose={() => setShowConfigEditModal(false)}
          onSave={handleSaveConfig}
          isSaving={saveConfigMutation.isPending}
        />
      )}

      {/* 조직 추가 모달 */}
      {showAddOrgModal && (
        <AddOrganizationToProductModal
          product={product}
          existingOrganizationIds={visibleOrganizations.map(org => org.organizationId)}
          onClose={() => setShowAddOrgModal(false)}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ['admin', 'products', product.id, 'organizations'] })
          }}
        />
      )}

      {purchaseOrg && (
        <PurchaseSubscriptionModal
          product={product}
          organization={purchaseOrg}
          onClose={() => setPurchaseOrg(null)}
          onSuccess={() => {
            setPurchaseOrg(null)
            toast.success('구매가 완료되었습니다!')
            queryClient.invalidateQueries({ queryKey: ['admin', 'subscriptions'] })
            queryClient.invalidateQueries({ queryKey: ['admin', 'products', product.id, 'organizations'] })
          }}
        />
      )}

      {/* 상품 제거 1단계 경고 모달 */}
      {deleteStep === 1 && createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4">
            <div className="flex items-center gap-3 p-6 border-b border-slate-200">
              <div className="p-2 bg-amber-100 rounded-full">
                <AlertTriangle className="w-5 h-5 text-amber-600" />
              </div>
              <h2 className="text-lg font-bold text-slate-900">상품 제거 경고</h2>
            </div>
            <div className="p-6 space-y-4">
              <p className="text-sm text-slate-900 font-semibold">
                「{product.name}」 상품을 제거할 수 없습니다.
              </p>
              <p className="text-sm text-slate-700">
                <span className="text-red-600 font-bold">사유: 구독중인 조직이 {visibleOrganizations.filter(org => org.hasActiveSubscription).length}개 있습니다.</span>
              </p>
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 space-y-1.5">
                {visibleOrganizations.filter(org => org.hasActiveSubscription).map(org => (
                  <div key={org.organizationId} className="flex items-center gap-2 text-sm">
                    <Building2 className="w-3.5 h-3.5 text-red-500 flex-shrink-0" />
                    <span className="text-red-700 font-medium">{org.organizationDisplayName || org.organizationName}</span>
                    <span className="text-red-400 text-xs">(구독중)</span>
                  </div>
                ))}
              </div>
              <p className="text-xs text-slate-500">
                제거 시 위 조직들의 구독(라이선스)과 클래스 연결이 모두 삭제됩니다.
              </p>
            </div>
            <div className="flex justify-end gap-3 p-6 border-t border-slate-200">
              <button
                type="button"
                onClick={() => setDeleteStep(0)}
                className="px-4 py-2 text-sm font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors"
              >
                취소
              </button>
              <button
                type="button"
                onClick={() => setDeleteStep(2)}
                className="px-4 py-2 text-sm font-medium text-amber-700 bg-amber-100 hover:bg-amber-200 rounded-lg border border-amber-300 transition-colors"
              >
                그래도 제거
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* 상품 제거 - 구독 없을 때 간단 확인 모달 */}
      {deleteStep === 3 && createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4">
            <div className="flex items-center gap-3 p-6 border-b border-slate-200">
              <div className="p-2 bg-red-100 rounded-full">
                <Trash2 className="w-5 h-5 text-red-600" />
              </div>
              <h2 className="text-lg font-bold text-slate-900">상품 삭제 확인</h2>
            </div>
            <div className="p-6">
              <p className="text-sm text-slate-700">
                「<span className="font-semibold">{product.name}</span>」 상품을 삭제하시겠습니까?
              </p>
              <p className="text-xs text-slate-500 mt-2">이 작업은 되돌릴 수 없습니다.</p>
            </div>
            <div className="flex justify-end gap-3 p-6 border-t border-slate-200">
              <button
                type="button"
                onClick={() => setDeleteStep(0)}
                className="px-4 py-2 text-sm font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors"
              >
                취소
              </button>
              <button
                type="button"
                onClick={() => {
                  setDeleteStep(0)
                  onDelete(product)
                }}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors"
              >
                삭제
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* 상품 제거 2단계 최종 확인 모달 */}
      {deleteStep === 2 && createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4">
            <div className="flex items-center gap-3 p-6 border-b border-red-200 bg-red-50 rounded-t-xl">
              <div className="p-2 bg-red-200 rounded-full">
                <AlertTriangle className="w-5 h-5 text-red-600" />
              </div>
              <h2 className="text-lg font-bold text-red-800">최종 확인</h2>
            </div>
            <div className="p-6 space-y-3">
              <p className="text-sm text-red-700 font-bold">
                구독하는 모든 조직에서 사라집니다
              </p>
              <p className="text-xs text-slate-600">
                이 작업은 되돌릴 수 없습니다. 상품 「{product.name}」와(과) 관련된 모든 라이선스 및 클래스 연결이 삭제됩니다.
              </p>
            </div>
            <div className="flex justify-end gap-3 p-6 border-t border-slate-200">
              <button
                type="button"
                onClick={() => setDeleteStep(0)}
                className="px-4 py-2 text-sm font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors"
              >
                제거 취소
              </button>
              <button
                type="button"
                onClick={() => {
                  setDeleteStep(0)
                  onDelete(product)
                }}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors"
              >
                제거 강행
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* 상품 제거 - 공개된 조직 있지만 구독은 없는 경우 */}
      {deleteStep === 4 && createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4">
            <div className="flex items-center gap-3 p-6 border-b border-slate-200">
              <div className="p-2 bg-amber-100 rounded-full">
                <AlertTriangle className="w-5 h-5 text-amber-600" />
              </div>
              <h2 className="text-lg font-bold text-slate-900">상품 삭제 확인</h2>
            </div>
            <div className="p-6 space-y-3">
              <p className="text-sm text-slate-900 font-semibold">
                「{product.name}」 상품에 공개된 조직이 {visibleOrganizations.length}개 있습니다.
              </p>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 space-y-1.5">
                {visibleOrganizations.map(org => (
                  <div key={org.organizationId} className="flex items-center gap-2 text-sm">
                    <Building2 className="w-3.5 h-3.5 text-amber-600 flex-shrink-0" />
                    <span className="text-amber-800 font-medium">{org.organizationDisplayName || org.organizationName}</span>
                  </div>
                ))}
              </div>
              <p className="text-xs text-slate-500">
                삭제하면 위 조직들의 라이선스 연결이 함께 제거됩니다.
              </p>
            </div>
            <div className="flex justify-end gap-3 p-6 border-t border-slate-200">
              <button
                type="button"
                onClick={() => setDeleteStep(0)}
                className="px-4 py-2 text-sm font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors"
              >
                취소
              </button>
              <button
                type="button"
                onClick={() => {
                  setDeleteStep(0)
                  onDelete(product)
                }}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors"
              >
                삭제
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* 조직 추가 - 필수 설정 누락 안내 모달 */}
      {missingRequirements.length > 0 && createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4">
            <div className="flex items-center gap-3 p-6 border-b border-slate-200">
              <div className="p-2 bg-amber-100 rounded-full">
                <AlertTriangle className="w-5 h-5 text-amber-600" />
              </div>
              <h2 className="text-lg font-bold text-slate-900">조직 공개 불가</h2>
            </div>
            <div className="p-6 space-y-4">
              <p className="text-sm text-slate-700">
                조직에 상품을 공개하려면 다음 설정을 먼저 완료해야 합니다:
              </p>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 space-y-2">
                {missingRequirements.map((req) => (
                  <div key={req} className="flex items-center gap-2 text-sm">
                    <X className="w-4 h-4 text-red-500 flex-shrink-0" />
                    <span className="text-amber-800 font-medium">{req}</span>
                  </div>
                ))}
              </div>
              <p className="text-xs text-slate-500">
                위 항목을 먼저 설정한 후 조직 추가를 진행해주세요.
              </p>
            </div>
            <div className="flex justify-end p-6 border-t border-slate-200">
              <button
                type="button"
                onClick={() => setMissingRequirements([])}
                className="px-4 py-2 text-sm font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors"
              >
                확인
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  )
}

// ============================================================================
// Product Form Modal (Simplified)
// ============================================================================
interface ProductFormModalProps {
  product: AdminProduct | null
  onClose: () => void
  onSave: (data: (CreateProductRequest | UpdateProductRequest) & { bundleContentIds?: number[] }) => void
  isLoading: boolean
  preselectedCurriculumId?: number
  preselectedCurriculumTitle?: string
}

function ProductFormModal({ product, onClose, onSave, isLoading, preselectedCurriculumId, preselectedCurriculumTitle }: ProductFormModalProps) {
  const [formData, setFormData] = useState<CreateProductRequest>({
    name: product?.name ?? (preselectedCurriculumTitle ? `${preselectedCurriculumTitle} 구독` : ''),
    slug: product?.slug ?? '',
    shortDescription: product?.shortDescription ?? '',
    description: product?.description ?? '',
    targetAudience: product?.targetAudience ?? 'individual',
    status: product?.status ?? 'draft',
    isActive: product?.isActive ?? true,
    isFeatured: product?.isFeatured ?? false,
    displayOrder: product?.displayOrder ?? 0,
    linkedContentId: product?.linkedContentId ?? preselectedCurriculumId ?? undefined,
    isBundle: product?.isBundle ?? false,
    allowConfigEdit: product?.allowConfigEdit ?? false,
    contentHierarchyLevel: product?.contentHierarchyLevel ?? 'curriculum',
    lcTrialSetCode: product?.lcTrialSetCode ?? undefined,
    lcTrialCurriculumId: product?.lcTrialCurriculumId ?? undefined,
    clearLcTrialConfiguration: false,
  })

  // Bundle용 선택된 컨텐츠 ID 목록 (순서 유지)
  const [bundleContentIds, setBundleContentIds] = useState<number[]>(
    product?.bundleContents?.map(c => c.contentId) ?? []
  )

  // 선택된 레벨에 맞는 컨텐츠 목록 조회
  const { data: contentsByLevel = [] } = useQuery({
    queryKey: ['admin', 'contents', formData.contentHierarchyLevel, 'all'],
    queryFn: () => adminApi.getContentsByLevel(formData.contentHierarchyLevel ?? 'curriculum', true),
  })

  const {
    data: lcTrialCurricula = [],
    isLoading: isLoadingLcTrialCurricula,
  } = useQuery<AdminLcTrialCurriculum[]>({
    queryKey: ['admin', 'products', 'lc-trial-curricula'],
    queryFn: adminApi.getLcTrialCurricula,
  })

  const CONTENT_LEVEL_LABELS: Record<string, string> = {
    curriculum: '커리큘럼',
    course: '코스',
    module: '모듈',
  }

  // 레벨 변경 시 선택된 컨텐츠 초기화
  const handleLevelChange = (level: string) => {
    setFormData({
      ...formData,
      contentHierarchyLevel: level as 'curriculum' | 'course' | 'module',
      linkedContentId: undefined,
    })
    setBundleContentIds([])
  }

  // Bundle에서 선택 가능한 컨텐츠 (이미 선택된 것 제외)
  const availableContents = contentsByLevel.filter(c => !bundleContentIds.includes(c.id))

  // Bundle 컨텐츠 추가
  const addBundleContent = (contentId: number) => {
    if (!bundleContentIds.includes(contentId)) {
      setBundleContentIds([...bundleContentIds, contentId])
    }
  }

  // Bundle 컨텐츠 제거
  const removeBundleContent = (contentId: number) => {
    setBundleContentIds(bundleContentIds.filter(id => id !== contentId))
  }

  // Bundle 컨텐츠 순서 변경
  const moveBundleContent = (index: number, direction: 'up' | 'down') => {
    const newIds = [...bundleContentIds]
    const newIndex = direction === 'up' ? index - 1 : index + 1
    if (newIndex >= 0 && newIndex < newIds.length) {
      [newIds[index], newIds[newIndex]] = [newIds[newIndex], newIds[index]]
      setBundleContentIds(newIds)
    }
  }

  // 컨텐츠 ID로 제목 가져오기
  const getContentTitle = (contentId: number): string => {
    const content = contentsByLevel.find(c => c.id === contentId)
    return content?.title ?? `컨텐츠 #${contentId}`
  }

  const levelLabel = CONTENT_LEVEL_LABELS[formData.contentHierarchyLevel ?? 'curriculum'] ?? '컨텐츠'

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    // 빈 문자열을 undefined로 변환하여 백엔드에서 null로 처리되도록 함
    const cleanedData = Object.fromEntries(
      Object.entries(formData).map(([key, value]) => [
        key,
        value === '' ? undefined : value,
      ])
    ) as CreateProductRequest & { bundleContentIds?: number[] }

    // Bundle 상품일 때 bundleContentIds 추가
    if (formData.isBundle) {
      cleanedData.bundleContentIds = bundleContentIds
    }

    onSave(cleanedData)
  }

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white flex items-center justify-between p-6 border-b border-slate-200 z-10">
          <h2 className="text-xl font-bold text-slate-900">
            {product ? '상품 수정' : '새 상품 만들기'}
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div className="space-y-4">
            {/* 1. 상품명 */}
            <div>
              <label className="label">상품명 *</label>
              <input
                type="text"
                className="input"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </div>

            {/* 2. 짧은 설명 */}
            <div>
              <label className="label">짧은 설명</label>
              <input
                type="text"
                className="input"
                value={formData.shortDescription ?? ''}
                onChange={(e) => setFormData({ ...formData, shortDescription: e.target.value })}
              />
            </div>

            {/* 3. 대상 고객 */}
            <div>
              <label className="label">대상 고객</label>
              <select
                className="input"
                value={formData.targetAudience ?? 'individual'}
                onChange={(e) => setFormData({ ...formData, targetAudience: e.target.value })}
              >
                {TARGET_AUDIENCE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>

            {/* 4. 연결 컨텐츠 */}
            <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg space-y-4">
              <h3 className="font-semibold text-purple-900 text-sm">연결 컨텐츠</h3>

              {/* 4.1 연결 컨텐츠 레벨 */}
              <div>
                <label className="label text-purple-800">컨텐츠 레벨</label>
                <select
                  className="input"
                  value={formData.contentHierarchyLevel ?? 'curriculum'}
                  onChange={(e) => handleLevelChange(e.target.value)}
                >
                  <option value="curriculum">커리큘럼</option>
                  <option value="course">코스</option>
                  <option value="module">모듈</option>
                </select>
              </div>

              {/* 4.2 번들 토글 */}
              <div className="flex items-center gap-3">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    className="w-5 h-5 rounded border-slate-300"
                    checked={formData.isBundle}
                    onChange={(e) => {
                      setFormData({
                        ...formData,
                        isBundle: e.target.checked,
                        linkedContentId: e.target.checked ? undefined : formData.linkedContentId,
                      })
                      if (!e.target.checked) {
                        setBundleContentIds([])
                      }
                    }}
                  />
                  <span className="font-medium text-purple-800">번들 (여러 개 선택)</span>
                </label>
              </div>

              {/* Bundle일 때 - 멀티 컨텐츠 선택 UI */}
              {formData.isBundle ? (
                <div className="space-y-3">
                  <div className="flex gap-2">
                    <select
                      className="input flex-1"
                      value=""
                      onChange={(e) => {
                        if (e.target.value) {
                          addBundleContent(Number(e.target.value))
                          e.target.value = ''
                        }
                      }}
                    >
                      <option value="">+ {levelLabel} 추가...</option>
                      {availableContents.map((content) => (
                        <option key={content.id} value={content.id}>
                          {content.title}
                        </option>
                      ))}
                    </select>
                  </div>

                  {bundleContentIds.length > 0 ? (
                    <div className="space-y-2">
                      <div className="text-xs font-medium text-purple-700">
                        선택된 {levelLabel} ({bundleContentIds.length}개)
                      </div>
                      <div className="space-y-1.5">
                        {bundleContentIds.map((contentId, index) => (
                          <div
                            key={contentId}
                            className="flex items-center gap-2 p-2 bg-white rounded-lg border border-purple-200 group"
                          >
                            <span className="w-6 h-6 flex items-center justify-center rounded bg-purple-100 text-purple-700 text-xs font-bold">
                              {index + 1}
                            </span>
                            <span className="flex-1 text-sm text-slate-700 truncate">
                              {getContentTitle(contentId)}
                            </span>
                            <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                              <button
                                type="button"
                                onClick={() => moveBundleContent(index, 'up')}
                                disabled={index === 0}
                                className="p-1 hover:bg-purple-100 rounded disabled:opacity-30 disabled:cursor-not-allowed"
                                title="위로 이동"
                              >
                                <ChevronUp className="w-4 h-4 text-purple-600" />
                              </button>
                              <button
                                type="button"
                                onClick={() => moveBundleContent(index, 'down')}
                                disabled={index === bundleContentIds.length - 1}
                                className="p-1 hover:bg-purple-100 rounded disabled:opacity-30 disabled:cursor-not-allowed"
                                title="아래로 이동"
                              >
                                <ChevronDown className="w-4 h-4 text-purple-600" />
                              </button>
                            </div>
                            <button
                              type="button"
                              onClick={() => removeBundleContent(contentId)}
                              className="p-1 hover:bg-red-100 rounded text-slate-400 hover:text-red-500 transition-colors"
                              title="삭제"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="text-sm text-purple-600/70 py-2">
                      위 드롭다운에서 {levelLabel}을(를) 선택하세요.
                    </div>
                  )}
                </div>
              ) : (
                /* 단일 컨텐츠 선택 */
                <div>
                  <label className="label text-purple-800">연결된 {levelLabel}</label>
                  <select
                    className="input"
                    value={formData.linkedContentId ?? ''}
                    onChange={(e) => setFormData({
                      ...formData,
                      linkedContentId: e.target.value ? Number(e.target.value) : undefined
                    })}
                  >
                    <option value="">선택 안 함</option>
                    {contentsByLevel.map((content) => (
                      <option key={content.id} value={content.id}>
                        {content.title}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            {/* LC 무료체험 전용 설정 */}
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 space-y-3">
              <div>
                <h3 className="text-sm font-semibold text-emerald-900">LC 무료체험 설정</h3>
                <p className="mt-1 text-xs text-emerald-700">
                  커리큘럼 빌더의 무료체험 과정을 선택하면 A~E 세트가 자동 지정됩니다. 위의 정규 연결 컨텐츠는 변경되지 않습니다. 최초 무료체험 부여 후에는 체험 과정을 변경할 수 없습니다.
                </p>
              </div>
              {product?.lcTrialLockedAt && (
                <div className="rounded bg-amber-100 px-3 py-2 text-xs font-medium text-amber-800">
                  무료체험에 사용된 설정입니다. 체험 과정과 세트는 변경할 수 없습니다.
                </div>
              )}
              <div>
                <label className="label text-emerald-900">무료체험 커리큘럼</label>
                <select
                  className="input"
                  value={formData.lcTrialCurriculumId ?? ''}
                  disabled={!!product?.lcTrialLockedAt}
                  onChange={(e) => {
                    const curriculumId = e.target.value ? Number(e.target.value) : undefined
                    const selectedCurriculum = lcTrialCurricula.find(
                      (curriculum) => curriculum.curriculumId === curriculumId
                    )
                    setFormData({
                      ...formData,
                      lcTrialCurriculumId: selectedCurriculum?.curriculumId,
                      lcTrialSetCode: selectedCurriculum?.trialSetCode,
                      clearLcTrialConfiguration: !selectedCurriculum,
                    })
                  }}
                >
                  <option value="">무료체험 미사용</option>
                  {lcTrialCurricula.map((curriculum) => (
                    <option
                      key={curriculum.curriculumId}
                      value={curriculum.curriculumId}
                      disabled={curriculum.lessonCount !== 5}
                    >
                      {curriculum.curriculumTitle} · 세트 {curriculum.trialSetCode} · {curriculum.lessonCount}개 레슨
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-emerald-700">
                  {isLoadingLcTrialCurricula
                    ? '무료체험 커리큘럼을 불러오는 중입니다.'
                    : '레슨이 정확히 5개인 무료체험 커리큘럼만 선택할 수 있습니다. 정식 전환 시 현재 상품이 기본 선택됩니다.'}
                </p>
              </div>
            </div>

            {/* 기타 설정 */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="label">상태</label>
                <select
                  className="input"
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value as ProductStatus })}
                >
                  {PRODUCT_STATUSES.map((status) => (
                    <option key={status} value={status}>{PRODUCT_STATUS_LABELS[status]}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="label">표시 순서</label>
                <input
                  type="number"
                  className="input"
                  value={formData.displayOrder || ''}
                  onChange={(e) => setFormData({ ...formData, displayOrder: parseInt(e.target.value) || 0 })}
                  onFocus={(e) => e.target.select()}
                  placeholder="0"
                />
              </div>
            </div>

            <div className="flex gap-6">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  className="w-5 h-5 rounded border-slate-300"
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                />
                <span className="text-sm font-medium text-slate-700">활성화</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  className="w-5 h-5 rounded border-slate-300"
                  checked={formData.isFeatured}
                  onChange={(e) => setFormData({ ...formData, isFeatured: e.target.checked })}
                />
                <span className="text-sm font-medium text-slate-700">추천 상품</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  className="w-5 h-5 rounded border-slate-300"
                  checked={formData.allowConfigEdit}
                  onChange={(e) => setFormData({ ...formData, allowConfigEdit: e.target.checked })}
                />
                <span className="text-sm font-medium text-slate-700">학습진행 설정 편집 허용</span>
                <span className="text-xs text-slate-500">(B2B Admin이 클래스별 설정 편집 가능)</span>
              </label>
            </div>

            {/* 가격 설정 안내 (새 상품 생성 시에만 표시) */}
            {!product && (
              <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <DollarSign className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm">
                  <p className="font-medium text-amber-800">가격은 상품 생성 후 설정해주세요</p>
                  <p className="text-amber-700 mt-1">
                    상품 목록에서 <strong>💲 버튼</strong>을 클릭하면 월간/연간 등 다양한 가격 옵션을 추가할 수 있습니다.
                  </p>
                </div>
              </div>
            )}
          </div>

          <div className="flex justify-end gap-3 pt-6 border-t border-slate-200">
            <button type="button" onClick={onClose} className="btn-secondary btn-md" disabled={isLoading}>
              취소
            </button>
            <button type="submit" className="btn-primary btn-md" disabled={isLoading || !formData.name}>
              {isLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
              {product ? '저장' : '생성'}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  )
}

// ============================================================================
// Main Component
// ============================================================================
export function ProductManagement() {
  const queryClient = useQueryClient()
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()
  const [page, setPage] = useState(0)
  const [keyword, setKeyword] = useState('')
  const [filterStatus, setFilterStatus] = useState<ProductStatus | ''>('')
  const [editingProduct, setEditingProduct] = useState<AdminProduct | null>(null)
  const [isCreating, setIsCreating] = useState(false)
  const [pricingProduct, setPricingProduct] = useState<AdminProduct | null>(null)
  const [preselectedCurriculumId, setPreselectedCurriculumId] = useState<number | undefined>(undefined)
  const [preselectedCurriculumTitle, setPreselectedCurriculumTitle] = useState<string | undefined>(undefined)
  
  // 체크박스 및 펼치기 상태
  const [selectedProducts, setSelectedProducts] = useState<number[]>([])
  const [expandedProducts, setExpandedProducts] = useState<number[]>([])
  const [statusDropdownId, setStatusDropdownId] = useState<number | null>(null)
  const [showOpenToOrgModal, setShowOpenToOrgModal] = useState(false)
  const [configModalProductId, setConfigModalProductId] = useState<number | null>(null)
  
  // 커리큘럼 트리 뷰어 상태
  const [treeViewerCurriculumId, setTreeViewerCurriculumId] = useState<number | null>(null)
  const [treeViewerTitle, setTreeViewerTitle] = useState<string>('')

  // URL 파라미터에서 createFor 읽어서 자동으로 새 상품 만들기 모달 열기
  useEffect(() => {
    const createFor = searchParams.get('createFor')
    const curriculumTitle = searchParams.get('curriculumTitle')
    
    if (createFor) {
      const curriculumId = parseInt(createFor)
      if (!isNaN(curriculumId)) {
        setPreselectedCurriculumId(curriculumId)
        setPreselectedCurriculumTitle(curriculumTitle || undefined)
        setIsCreating(true)
        // URL에서 파라미터 제거
        searchParams.delete('createFor')
        searchParams.delete('curriculumTitle')
        setSearchParams(searchParams, { replace: true })
      }
    }
  }, [searchParams, setSearchParams])

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'products', { page, keyword, filterStatus }],
    queryFn: () => adminApi.getProducts({
      page,
      size: 15,
      keyword: keyword || undefined,
      status: filterStatus || undefined,
    }),
    staleTime: 30 * 1000,
  })

  // 커리큘럼 계층 구조 조회
  const { data: hierarchyData, isLoading: isHierarchyLoading } = useQuery({
    queryKey: ['curriculum-hierarchy', treeViewerCurriculumId],
    queryFn: () => adminApi.getContentHierarchy(treeViewerCurriculumId!),
    enabled: !!treeViewerCurriculumId,
  })

  // 목록에서 연 학습진행 설정 모달용 configuration 조회
  const { data: configModalConfig } = useQuery({
    queryKey: ['configuration', 'product', configModalProductId],
    queryFn: () => configApi.getProductConfiguration(configModalProductId!),
    enabled: !!configModalProductId,
    staleTime: 0,
  })

  const saveConfigFromListMutation = useMutation({
    mutationFn: (config: LessonConfiguration) =>
      configModalProductId != null
        ? configApi.setProductConfiguration(configModalProductId, config)
        : Promise.reject(new Error('상품이 선택되지 않았습니다')),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['configuration', 'product'] })
      setConfigModalProductId(null)
    },
    onError: (err) => {
      console.error('Configuration 저장 실패:', err)
      alert('학습진행 설정 저장에 실패했습니다.')
    },
  })

  const createProductMutation = useMutation({
    mutationFn: async (data: CreateProductRequest & { bundleContentIds?: number[] }) => {
      const { bundleContentIds, ...productData } = data
      const createdProduct = await adminApi.createProduct(productData)

      // Bundle 상품이고 bundleContentIds가 있으면 콘텐츠 추가
      if (productData.isBundle && bundleContentIds && bundleContentIds.length > 0) {
        await adminApi.addProductContents(createdProduct.id, { contentIds: bundleContentIds })
        // 순서 맞추기
        const contentOrders = bundleContentIds.map((contentId, index) => ({
          contentId,
          sequenceOrder: index + 1,
        }))
        await adminApi.reorderProductContents(createdProduct.id, { contentOrders })
      }

      return createdProduct
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'products'] })
      setIsCreating(false)
    },
    onError: (error) => {
      console.error('상품 생성 실패:', error)
      alert('상품 생성에 실패했습니다. 다시 시도해주세요.')
    },
  })

  const updateProductMutation = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: UpdateProductRequest & { bundleContentIds?: number[] } }) => {
      const { bundleContentIds, ...productData } = data
      const updatedProduct = await adminApi.updateProduct(id, productData)

      // Bundle 상품이고 bundleContentIds가 정의되어 있으면 콘텐츠 동기화
      if (productData.isBundle && bundleContentIds !== undefined) {
        // 현재 상품의 bundle contents 조회
        const currentContents = await adminApi.getProductContents(id)
        const currentIds = currentContents.map(c => c.contentId)

        // 삭제할 콘텐츠 (현재에 있지만 새 목록에 없는 것)
        const toRemove = currentIds.filter(cid => !bundleContentIds.includes(cid))
        for (const contentId of toRemove) {
          await adminApi.removeProductContent(id, contentId)
        }

        // 추가할 콘텐츠 (새 목록에 있지만 현재에 없는 것)
        const toAdd = bundleContentIds.filter(cid => !currentIds.includes(cid))
        if (toAdd.length > 0) {
          await adminApi.addProductContents(id, { contentIds: toAdd })
        }

        // 순서 업데이트
        if (bundleContentIds.length > 0) {
          const contentOrders = bundleContentIds.map((contentId, index) => ({
            contentId,
            sequenceOrder: index + 1,
          }))
          await adminApi.reorderProductContents(id, { contentOrders })
        }
      }

      return updatedProduct
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'products'] })
      setEditingProduct(null)
    },
    onError: (error) => {
      console.error('상품 수정 실패:', error)
      alert('상품 수정에 실패했습니다. 다시 시도해주세요.')
    },
  })

  const deleteProductMutation = useMutation({
    mutationFn: (id: number) => adminApi.deleteProduct(id),
    onSuccess: () => {
      toast.success('상품이 삭제되었습니다.')
      queryClient.invalidateQueries({ queryKey: ['admin', 'products'] })
    },
    onError: (error) => {
      console.error('상품 삭제 실패:', error)
      toast.error('상품 삭제에 실패했습니다. 다시 시도해주세요.')
    },
  })

  const toggleStatusMutation = useMutation({
    mutationFn: (id: number) => adminApi.toggleProductStatus(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'products'] }),
  })

  // 상품 상태 변경 (draft → active 등)
  const changeProductStatusMutation = useMutation({
    mutationFn: ({ id, status }: { id: number; status: ProductStatus }) =>
      adminApi.updateProduct(id, { status }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'products'] })
    },
    onError: (error) => {
      console.error('상품 상태 변경 실패:', error)
      alert('상품 상태 변경에 실패했습니다. 다시 시도해주세요.')
    },
  })

  const bulkActivateMutation = useMutation({
    mutationFn: (productIds: number[]) => adminApi.bulkActivateProducts(productIds),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'products'] })
      setSelectedProducts([])
      alert(`${result.activatedCount}개 상품이 활성화되었습니다.`)
    },
  })

  // 체크박스 유틸리티
  const toggleProductSelection = (productId: number) => {
    setSelectedProducts(prev =>
      prev.includes(productId)
        ? prev.filter(id => id !== productId)
        : [...prev, productId]
    )
  }

  const toggleAllSelection = () => {
    if (data?.content) {
      if (selectedProducts.length === data.content.length) {
        setSelectedProducts([])
      } else {
        setSelectedProducts(data.content.map(p => p.id))
      }
    }
  }

  // 펼치기 토글
  const toggleExpand = (productId: number) => {
    setExpandedProducts(prev =>
      prev.includes(productId)
        ? prev.filter(id => id !== productId)
        : [...prev, productId]
    )
  }

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    setPage(0)
  }

  const handleDelete = (product: AdminProduct) => {
    deleteProductMutation.mutate(product.id)
  }

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px]">
        <Loader2 className="w-10 h-10 text-slate-600 animate-spin mb-4" />
        <p className="text-slate-600">상품 목록을 불러오는 중...</p>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px]">
        <AlertCircle className="w-12 h-12 text-red-500 mb-4" />
        <p className="text-slate-900 font-medium mb-2">데이터를 불러올 수 없습니다</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Package className="w-7 h-7 text-amber-600" />
            상품 관리
          </h1>
          <p className="text-slate-600 mt-1">총 {data?.totalElements ?? 0}개의 상품</p>
        </div>
        <button onClick={() => setIsCreating(true)} className="btn-primary btn-md">
          <Plus className="w-5 h-5 mr-2" />
          새 상품
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-slate-200 p-4 space-y-4">
        <form onSubmit={handleSearch} className="flex flex-wrap gap-4">
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <input
                type="text"
                placeholder="상품명 검색..."
                className="input pl-10"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
              />
            </div>
          </div>
          <select
            className="input w-32"
            value={filterStatus}
            onChange={(e) => { setFilterStatus(e.target.value as ProductStatus | ''); setPage(0) }}
          >
            <option value="">모든 상태</option>
            {PRODUCT_STATUSES.map((status) => (
              <option key={status} value={status}>{PRODUCT_STATUS_LABELS[status]}</option>
            ))}
          </select>
          <button type="submit" className="btn-primary btn-md">
            검색
          </button>
        </form>

        {/* Bulk Actions */}
        {selectedProducts.length > 0 && (
          <div className="flex items-center gap-3 pt-3 border-t border-slate-200">
            <span className="text-sm text-slate-600 font-medium">
              {selectedProducts.length}개 선택됨
            </span>
            <button
              onClick={() => bulkActivateMutation.mutate(selectedProducts)}
              disabled={bulkActivateMutation.isPending}
              className="btn-secondary btn-sm flex items-center gap-2"
            >
              {bulkActivateMutation.isPending ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Zap className="w-4 h-4" />
              )}
              활성화
            </button>
            <button
              onClick={() => setShowOpenToOrgModal(true)}
              className="btn-secondary btn-sm flex items-center gap-2"
            >
              <Building2 className="w-4 h-4" />
              Open to Org
            </button>
            <button
              onClick={() => setSelectedProducts([])}
              className="text-sm text-slate-500 hover:text-slate-700"
            >
              선택 해제
            </button>
          </div>
        )}
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-center px-2 py-3 w-10">
                  <button
                    onClick={toggleAllSelection}
                    className="p-1 hover:bg-slate-100 rounded"
                    title={selectedProducts.length === data?.content.length ? "전체 해제" : "전체 선택"}
                  >
                    {selectedProducts.length === data?.content.length && data?.content.length > 0 ? (
                      <CheckSquare className="w-5 h-5 text-blue-600" />
                    ) : (
                      <Square className="w-5 h-5 text-slate-400" />
                    )}
                  </button>
                </th>
                <th className="text-center px-1 py-3 w-8"></th>
                <th className="text-left px-3 py-3 text-sm font-semibold text-slate-700 min-w-[140px]">상품명(ID)</th>
                <th className="text-left px-3 py-3 text-sm font-semibold text-slate-700 min-w-[120px]">연결된 콘텐츠</th>
                <th className="text-left px-3 py-3 text-sm font-semibold text-slate-700 w-28">가격</th>
                <th className="text-center px-3 py-3 text-sm font-semibold text-slate-700 w-20">학습진행</th>
                <th className="text-center px-3 py-3 text-sm font-semibold text-slate-700 w-16">활성</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {data?.content.map((product) => {
                const formatPrice = (price: number) =>
                  new Intl.NumberFormat('ko-KR').format(price)
                const isSelected = selectedProducts.includes(product.id)
                const isExpanded = expandedProducts.includes(product.id)

                return (
                  <React.Fragment key={product.id}>
                    <tr className={`hover:bg-slate-50 ${isSelected ? 'bg-blue-50' : ''}`}>
                      <td className="text-center px-2 py-3">
                        <button
                          onClick={() => toggleProductSelection(product.id)}
                          className="p-1 hover:bg-slate-100 rounded"
                        >
                          {isSelected ? (
                            <CheckSquare className="w-5 h-5 text-blue-600" />
                          ) : (
                            <Square className="w-5 h-5 text-slate-400" />
                          )}
                        </button>
                      </td>
                      <td className="text-center px-1 py-3">
                        <button
                          onClick={() => toggleExpand(product.id)}
                          className={`p-1.5 rounded-lg border transition-all ${
                            isExpanded
                              ? 'bg-blue-100 border-blue-300 text-blue-600'
                              : 'bg-slate-100 border-slate-300 text-slate-600 hover:bg-blue-50 hover:border-blue-300 hover:text-blue-600'
                          }`}
                          title={isExpanded ? "접기" : "펼치기"}
                        >
                          {isExpanded ? (
                            <ChevronUp className="w-5 h-5" />
                          ) : (
                            <ChevronDown className="w-5 h-5" />
                          )}
                        </button>
                      </td>
                      <td className="px-3 py-3">
                        <div>
                          <p className="text-sm font-semibold text-slate-900">{product.name} ({product.id})</p>
                          <div className="flex items-center gap-2 mt-2">
                            {/* 상태 변경 드롭다운 */}
                            <div className="relative">
                              <button
                                onClick={() => setStatusDropdownId(statusDropdownId === product.id ? null : product.id)}
                                className={`px-2 py-0.5 rounded-full text-xs font-medium ${PRODUCT_STATUS_COLORS[product.status]} hover:ring-2 hover:ring-offset-1 hover:ring-slate-300 transition-all`}
                                title="상품 라이프사이클 (초안→활성→보관→종료)"
                              >
                                {PRODUCT_STATUS_LABELS[product.status]}
                                <ChevronDown className="w-3 h-3 inline-block ml-0.5" />
                              </button>
                              {statusDropdownId === product.id && (
                                <>
                                  <div className="fixed inset-0 z-10" onClick={() => setStatusDropdownId(null)} />
                                  <div className="absolute left-0 top-full mt-1 z-20 bg-white border border-slate-200 rounded-lg shadow-lg min-w-[100px]">
                                    {PRODUCT_STATUSES.filter(s => s !== product.status).map((status) => (
                                      <button
                                        key={status}
                                        onClick={() => {
                                          changeProductStatusMutation.mutate({ id: product.id, status })
                                          setStatusDropdownId(null)
                                        }}
                                        className={`w-full px-3 py-1.5 text-left text-xs font-medium hover:bg-slate-50 first:rounded-t-lg last:rounded-b-lg ${PRODUCT_STATUS_COLORS[status]}`}
                                        disabled={changeProductStatusMutation.isPending}
                                      >
                                        {PRODUCT_STATUS_LABELS[status]}
                                      </button>
                                    ))}
                                  </div>
                                </>
                              )}
                            </div>
                            {/* draft 상태일 때 빠른 활성화 버튼 */}
                            {product.status === 'draft' && (
                              <button
                                onClick={() => changeProductStatusMutation.mutate({ id: product.id, status: 'active' })}
                                disabled={changeProductStatusMutation.isPending}
                                className="px-2 py-0.5 rounded-full text-xs font-medium bg-green-600 text-white hover:bg-green-700 transition-colors flex items-center gap-1"
                                title="상품 활성화"
                              >
                                {changeProductStatusMutation.isPending ? (
                                  <Loader2 className="w-3 h-3 animate-spin" />
                                ) : (
                                  <Zap className="w-3 h-3" />
                                )}
                                활성화
                              </button>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-3">
                        {product.isBundle && product.bundleContents && product.bundleContents.length > 0 ? (
                          <div className="space-y-0.5">
                            {product.bundleContents.map((content) => (
                              <div key={content.contentId} className="flex items-center gap-1 min-w-0">
                                <BookOpen className="w-3.5 h-3.5 text-green-600 flex-shrink-0" />
                                <span className="text-sm text-slate-700 whitespace-nowrap">{content.contentTitle || `콘텐츠 #${content.contentId}`}</span>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    setTreeViewerCurriculumId(content.contentId)
                                    setTreeViewerTitle(content.contentTitle || '커리큘럼 구조')
                                  }}
                                  className="p-1 text-purple-500 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                                  title="커리큘럼 구조 보기"
                                >
                                  <ListTree className="w-4 h-4" />
                                </button>
                              </div>
                            ))}
                          </div>
                        ) : product.linkedContentTitle ? (
                          <div className="flex items-center gap-2 min-w-0">
                            <BookOpen className="w-4 h-4 text-green-600 flex-shrink-0" />
                            <span className="text-sm text-slate-700 truncate">{product.linkedContentTitle}</span>
                            {product.linkedContentId && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation()
                                  setTreeViewerCurriculumId(product.linkedContentId!)
                                  setTreeViewerTitle(product.linkedContentTitle || '커리큘럼 구조')
                                }}
                                className="p-1 text-purple-500 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                                title="커리큘럼 구조 보기"
                              >
                                <ListTree className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        ) : (
                          <span className="text-xs text-slate-400">연결 안 됨</span>
                        )}
                      </td>
                      <td className="px-3 py-3 min-w-0">
                        <div>
                          {product.pricings && product.pricings.length > 0 ? (
                            <>
                              <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5">
                                {product.pricings.map((pricing) => (
                                  <span key={pricing.id} className="text-sm text-slate-700 whitespace-nowrap">
                                    • {pricing.name}
                                  </span>
                                ))}
                              </div>
                            </>
                          ) : (
                            <button
                              onClick={() => setPricingProduct(product)}
                              className="px-2 py-1 text-xs font-medium text-red-600 bg-red-50 hover:bg-red-100 rounded border border-red-200 transition-colors"
                            >
                              가격설정
                            </button>
                          )}
                        </div>
                      </td>
                      <ProductConfigCell product={product} onOpenConfig={(p) => setConfigModalProductId(p.id)} />
                      <td className="px-3 py-3 text-center">
                        <button
                          onClick={() => toggleStatusMutation.mutate(product.id)}
                          className="p-1 rounded hover:bg-slate-100"
                          title={`노출 여부: ${product.isActive ? 'ON' : 'OFF'} (클라이언트에게 보이는지 여부)`}
                        >
                          {product.isActive ? (
                            <ToggleRight className="w-6 h-6 text-green-600" />
                          ) : (
                            <ToggleLeft className="w-6 h-6 text-slate-400" />
                          )}
                        </button>
                      </td>
                    </tr>
                    {isExpanded && (
                        <ProductDetailPanel
                          product={product}
                          onEdit={setEditingProduct}
                          onDelete={handleDelete}
                          onPricingManage={setPricingProduct}
                        />
                      )}
                  </React.Fragment>
                )
              })}
            </tbody>
          </table>
        </div>

        {/* Empty State */}
        {data?.content.length === 0 && (
          <div className="text-center py-12">
            <Package className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">등록된 상품이 없습니다</p>
            <button onClick={() => setIsCreating(true)} className="btn-primary btn-md mt-4">
              <Plus className="w-4 h-4 mr-2" />
              첫 상품 만들기
            </button>
          </div>
        )}

        {/* Open to Org Modal */}
        {showOpenToOrgModal && selectedProducts.length > 0 && (
          <OpenToOrgModal
            productIds={selectedProducts}
            onClose={() => setShowOpenToOrgModal(false)}
            onSuccess={() => {
              queryClient.invalidateQueries({ queryKey: ['admin', 'products'] })
              setSelectedProducts([])
            }}
          />
        )}

        {/* Pagination */}
        {data && data.totalPages > 1 && (
          <div className="flex items-center justify-between px-6 py-4 border-t border-slate-200">
            <p className="text-sm text-slate-600">
              {data.page * data.size + 1} - {Math.min((data.page + 1) * data.size, data.totalElements)} / {data.totalElements}
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage(page - 1)}
                disabled={data.first}
                className="p-2 hover:bg-slate-100 rounded-lg disabled:opacity-50"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <span className="text-sm text-slate-600">
                {data.page + 1} / {data.totalPages}
              </span>
              <button
                onClick={() => setPage(page + 1)}
                disabled={data.last}
                className="p-2 hover:bg-slate-100 rounded-lg disabled:opacity-50"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      {(isCreating || editingProduct) && (
        <ProductFormModal
          product={editingProduct}
          onClose={() => {
            setIsCreating(false)
            setEditingProduct(null)
            setPreselectedCurriculumId(undefined)
            setPreselectedCurriculumTitle(undefined)
          }}
          onSave={(data) => {
            if (editingProduct) {
              updateProductMutation.mutate({ id: editingProduct.id, data })
            } else {
              createProductMutation.mutate(data as CreateProductRequest)
            }
          }}
          isLoading={createProductMutation.isPending || updateProductMutation.isPending}
          preselectedCurriculumId={!editingProduct ? preselectedCurriculumId : undefined}
          preselectedCurriculumTitle={!editingProduct ? preselectedCurriculumTitle : undefined}
        />
      )}

      {/* Pricing Management Modal */}
      {pricingProduct && (
        <PricingManagementModal
          product={pricingProduct}
          onClose={() => setPricingProduct(null)}
        />
      )}

      {/* 학습진행 설정 모달 (목록에서 열림) */}
      {configModalProductId && (
        <ConfigurationEditModal
          targetType="product"
          targetId={configModalProductId}
          targetName={data?.content?.find((p) => p.id === configModalProductId)?.name ?? ''}
          configuration={configModalConfig ?? null}
          onClose={() => setConfigModalProductId(null)}
          onSave={async (config) => {
            await saveConfigFromListMutation.mutateAsync(config)
          }}
          isSaving={saveConfigFromListMutation.isPending}
        />
      )}

      {/* 커리큘럼 트리 뷰어 */}
      {hierarchyData && (
        <CurriculumTreeViewer
          data={hierarchyData}
          isOpen={!!treeViewerCurriculumId}
          onClose={() => setTreeViewerCurriculumId(null)}
          title={treeViewerTitle}
        />
      )}

      {/* 트리 뷰어 로딩 중 */}
      {treeViewerCurriculumId && isHierarchyLoading && createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-lg p-6 flex items-center gap-3">
            <Loader2 className="w-6 h-6 text-purple-600 animate-spin" />
            <span className="text-gray-700">커리큘럼 구조 로딩 중...</span>
          </div>
        </div>,
        document.body
      )}
    </div>
  )
}
